import axios, { AxiosInstance } from 'axios';

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    // Any successful write may have added or changed a vendor/unit/category, so drop the lookups cache.
    if (response.config.method && response.config.method.toLowerCase() !== 'get') clearLookupsCache();
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username: string, password: string) => {
  const { data } = await api.post('/auth/login', { email: username, password });
  return data;
};

export const getMe = async () => {
  const { data } = await api.get('/auth/me');
  return data.user;
};

// Dashboard
export const getDashboard = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  startDate?: string;
  endDate?: string;
}) => {
  const { data } = await api.get('/dashboard', { params });
  return data;
};

// Material Requests

/** Decimals arrive as strings over JSON; every money/qty field is read through Number(). */
export type Numeric = number | string;

/** Catalog link carried by MR lines and by the MR line behind a PO line. */
export interface ListCatalogItemRef {
  id: number;
  partNumber: string;
  description: string | null;
  descriptionAr: string | null;
}

/** Vendor as it comes back inside list rows (Prisma Vendor, so appCode/nameAr are present). */
export interface ListVendorRef {
  id: number;
  name: string;
  code?: string | null;
  appCode?: string | null;
  nameAr?: string | null;
  [key: string]: any;
}

export interface MRListLine {
  id: number;
  lineNo: number;
  partNumber: string | null;
  itemDescription: string;
  qty: Numeric;
  unitPrice: Numeric;
  lineTotal: Numeric;
  lineApprovalStatus: string;
  lineStatus: string;
  vendorSource: string | null;
  origUnitPrice: Numeric | null;
  refinedAt: string | null;
  catalogItem: ListCatalogItemRef | null;
  vendor: ListVendorRef | null;
  unit: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  costCenter: any | null;
  poLines?: Array<{
    id: number;
    poHeaderId: number;
    poHeader: { id: number; poId: string; status: string } | null;
    qty: Numeric;
    unitPrice: Numeric;
    lineTotal: Numeric;
    actualUnitPrice: Numeric | null;
    actualLineTotal: Numeric | null;
  }>;
  [key: string]: any;
}

export interface MRListRow {
  id: number;
  mrId: string;
  department: { id: number; code: string; name: string } | null;
  requestedBy: { id: number; displayName: string } | null;
  requestDate: string;
  neededBy: string | null;
  createdAt: string;
  urgency: string;
  status: string;
  mrTotal: Numeric | null;
  actualTotal: Numeric | null;
  /** Net of cancelled (2026-09-22): requested − cancelled lines − parts cancelled on a PO as not needed. */
  netTotal?: number;
  cancelledTotal?: number;
  /** Kept share of the linked PO lines at actual price; null when no PO line has an actual yet. */
  netActualTotal?: number | null;
  firstPoId: string | null;
  lineCount: number;
  poCount: number;
  lines: MRListLine[];
  [key: string]: any;
}

/** Counts for the same department/date scope, ignoring the status filter. */
export interface MRListAttention {
  submitted: number;
  approvedNotOrdered: number;
  neededByPassed: number;
  /** MRs with a line cancellation waiting for the CEO (2026-09-15). */
  lineCancellations?: number;
}

/** Ask to cancel an approved, not-ordered MR line (requester of the MR, procurement, CEO). Returns the refreshed MR. */
export const requestMRLineCancel = async (mrId: number | string, lineId: number, reason: string) => {
  const { data } = await api.post(`/mrs/${mrId}/lines/${lineId}/cancel-request`, { reason });
  return data;
};

/** CEO / admin: approve or reject a pending request; approve with a reason and no request = direct cancel. Returns the refreshed MR. */
export const decideMRLineCancel = async (
  mrId: number | string,
  lineId: number,
  body: { decision: 'approve' | 'reject'; reason?: string },
) => {
  const { data } = await api.post(`/mrs/${mrId}/lines/${lineId}/cancel-decision`, body);
  return data;
};

export interface MRListResponse {
  data: MRListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  attention: MRListAttention;
}

export const listMRs = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  status?: string;
  statuses?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  lineCancellations?: string;
}): Promise<MRListResponse> => {
  const { data } = await api.get('/mrs', { params });
  return data;
};

export const getMR = async (mrId: number | string) => {
  const { data } = await api.get(`/mrs/${mrId}`);
  return data;
};

export const createMR = async (mrData: {
  departmentId: number;
  neededBy: string;
  urgency: string;
  notes?: string;
  lines: Array<{
    partNumber?: string;
    itemDescription: string;
    categoryId?: number;
    vendorId?: number;
    vendorName?: string;
    qty: number;
    unitId?: number;
    unitPrice: number;
    currencyId?: number;
    lineNotes?: string;
  }>;
}) => {
  const { data } = await api.post('/mrs', mrData);
  return data;
};

export const updateMR = async (mrId: string | number, mrData: Record<string, any>) => {
  const { data } = await api.put(`/mrs/${mrId}`, mrData);
  return data;
};

export const submitMR = async (mrId: number) => {
  const { data } = await api.post(`/mrs/${mrId}/submit`);
  return data;
};

/** Pull a Submitted MR back to Draft (requester only, while no line has been acted on). */
export const recallMR = async (mrId: number) => {
  const { data } = await api.post(`/mrs/${mrId}/recall`);
  return data;
};

export const approveLines = async (
  mrId: number,
  approvals: Array<{
    lineId: number;
    approved: boolean;
    rejectReason?: string;
  }>
) => {
  const { data } = await api.post(`/mrs/${mrId}/approve`, { approvals });
  return data;
};

export const getApprovableLines = async (departmentId?: number) => {
  const { data } = await api.get('/mrs/approvable', {
    params: departmentId ? { departmentId } : undefined,
  });
  return data;
};

// Purchase Orders

export interface POListLine {
  id: number;
  lineNo: number;
  partNumber: string | null;
  itemDescription: string;
  qty: Numeric;
  unitPrice: Numeric;
  lineTotal: Numeric;
  actualUnitPrice: Numeric | null;
  actualLineTotal: Numeric | null;
  totalReceived: Numeric;
  balanceQty: Numeric;
  unit: { name: string } | null;
  currency: { code: string } | null;
  costCenter: any | null;
  mrLine: {
    lineNo: number;
    catalogItem: ListCatalogItemRef | null;
    mrHeader: { mrId: string; department: { id: number; code: string; name: string } | null } | null;
  } | null;
  [key: string]: any;
}

export interface POListRow {
  id: number;
  poId: string;
  department: { id: number; code: string; name: string } | null;
  vendor: ListVendorRef | null;
  createdBy: { id: number; displayName: string } | null;
  poDate: string;
  createdAt: string;
  status: string;
  poTotal: Numeric | null;
  actualTotal: Numeric | null;
  receivedValue: Numeric;
  openValue: Numeric;
  /** Net of cancelled (2026-09-22): shown total − cancelled part; stored totals stay as ordered. */
  netValue?: number;
  cancelledValue?: number;
  netEstimate?: number;
  cancelledEstimate?: number;
  lineCount: number;
  firstMrId: string | null;
  lateFlag: boolean;
  overdueFlag: boolean;
  poAgeDays: number | null;
  lines: POListLine[];
  [key: string]: any;
}

/** Counts for the same department/date scope, ignoring the status filter. */
export interface POListAttention {
  overEstimate: number;
  late: number;
  openValue: number;
  /** POs with a line cancellation waiting for approval (2026-09-14). */
  lineCancellations?: number;
}

export interface POListResponse {
  data: POListRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  attention: POListAttention;
}

export const listPOs = async (params?: {
  departmentId?: number;
  departmentIds?: string;
  status?: string;
  statuses?: string;
  vendorId?: number;
  vendorIds?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<POListResponse> => {
  const { data } = await api.get('/pos', { params });
  return data;
};

export const getPO = async (poId: number | string) => {
  const { data } = await api.get(`/pos/${poId}`);
  return data;
};

export const createPO = async (mrLineIds: number[], notes?: string, reviewed?: boolean) => {
  const { data } = await api.post('/pos', { mrLineIds, notes, reviewed });
  return data;
};

export const updatePOLines = async (
  poId: string,
  updates: Array<{ poLineId: number; partNumber?: string; actualUnitPrice?: number }>
) => {
  const { data } = await api.put(`/pos/${poId}/lines`, { updates });
  return data;
};

export const updatePOStatus = async (poId: number | string, status: string, reason?: string, mrAction?: 'reorder' | 'not_needed') => {
  const { data } = await api.put(`/pos/${poId}/status`, { status, reason, mrAction });
  return data;
};

export const receivePO = async (
  poId: number | string,
  items: Array<{ poLineId: number; receivedQty: number; notes?: string }>
) => {
  const { data } = await api.post(`/pos/${poId}/receive`, { items });
  return data;
};

// Lookups — cached per signed-in token for 5 minutes (2026-09-25): 31 screens called this on every mount,
// firing 5 requests each time. Cleared by any successful write (see the response interceptor).
const LOOKUPS_TTL_MS = 5 * 60 * 1000;
let lookupsCache: { token: string | null; at: number; promise: ReturnType<typeof fetchLookups> } | null = null;

export function clearLookupsCache() {
  lookupsCache = null;
}

export const getLookups = async () => {
  const token = localStorage.getItem('token');
  if (!lookupsCache || lookupsCache.token !== token || Date.now() - lookupsCache.at > LOOKUPS_TTL_MS) {
    const entry = { token, at: Date.now(), promise: fetchLookups() };
    lookupsCache = entry;
    entry.promise.catch(() => { if (lookupsCache === entry) lookupsCache = null; });
  }
  const l = await lookupsCache.promise;
  // Fresh arrays per caller so a screen that edits its copy cannot change what the next screen sees.
  return {
    departments: [...(l.departments || [])],
    vendors: [...(l.vendors || [])],
    categories: [...l.categories],
    categoryTree: [...l.categoryTree],
    units: [...(l.units || [])],
    currencies: [...(l.currencies || [])],
  };
};

const fetchLookups = async () => {
  const [departments, vendors, categories, units, currencies] =
    await Promise.all([
      api.get('/lookups/departments'),
      api.get('/lookups/vendors'),
      api.get('/lookups/categories'),
      api.get('/lookups/units'),
      api.get('/lookups/currencies'),
    ]);
  return {
    departments: departments.data,
    vendors: vendors.data,
    categories: Array.isArray(categories.data) ? categories.data : (categories.data?.flat || []),
    categoryTree: Array.isArray(categories.data) ? [] : (categories.data?.tree || []),
    units: units.data,
    currencies: currencies.data,
  };
};

// Admin - CRUD for lookups
export const createVendor = async (vendorData: { name: string; code?: string; contactInfo?: string }) => {
  const { data } = await api.post('/lookups/vendors', vendorData);
  return data;
};

export const createCategory = async (categoryData: { name: string; code?: string; parentId?: number; description?: string }) => {
  const { data } = await api.post('/lookups/categories', categoryData);
  return data;
};

export const createUnit = async (unitData: { name: string; abbreviation?: string }) => {
  const { data } = await api.post('/lookups/units', unitData);
  return data;
};

export const listUsers = async () => {
  const { data } = await api.get('/auth/users');
  return data;
};

export const createUser = async (userData: {
  email: string;
  password: string;
  displayName: string;
  roles: string[];
  departmentId: number;
  extraDepartmentIds?: number[];
}) => {
  const { data } = await api.post('/auth/register', userData);
  return data;
};

export const updateUser = async (userId: number, data: { displayName?: string; email?: string; role?: string; roles?: string[]; departmentId?: number; extraDepartmentIds?: number[]; isActive?: boolean; password?: string }) => {
  const { data: result } = await api.put(`/auth/users/${userId}`, data);
  return result;
};

export const deleteUser = async (userId: number, force?: boolean) => {
  const { data } = await api.delete(`/auth/users/${userId}`, { params: force ? { force: 'true' } : undefined });
  return data;
};

// Permissions
export const getMyPermissions = async (): Promise<Record<string, string>> => {
  const { data } = await api.get('/permissions/my');
  return data;
};

export const listAllPermissions = async (filters?: { grantType?: string; grantValue?: string }) => {
  const { data } = await api.get('/permissions', { params: filters });
  return data;
};

export const listResources = async () => {
  const { data } = await api.get('/permissions/resources');
  return data;
};

export const upsertPermission = async (perm: { resource: string; accessLevel: string; grantType: string; grantValue: string }) => {
  const { data } = await api.put('/permissions', perm);
  return data;
};

export const deletePermission = async (id: number) => {
  const { data } = await api.delete(`/permissions/${id}`);
  return data;
};

export const amendMRLine = async (mrId: number | string, lineId: number, updates: { qty?: number; rejectReason?: string; lineApprovalStatus?: string }) => {
  const { data } = await api.put(`/mrs/${mrId}/lines/${lineId}`, updates);
  return data;
};

export const updateVendor = async (vendorId: number, data: { name?: string; code?: string; contactInfo?: string }) => {
  const { data: result } = await api.put(`/lookups/vendors/${vendorId}`, data);
  return result;
};

export const deleteVendor = async (vendorId: number) => {
  const { data } = await api.delete(`/lookups/vendors/${vendorId}`);
  return data;
};

export const updateCategory = async (categoryId: number, data: { name?: string; code?: string; parentId?: number; description?: string; isActive?: boolean }) => {
  const { data: result } = await api.put(`/lookups/categories/${categoryId}`, data);
  return result;
};

export const deleteCategory = async (categoryId: number) => {
  const { data } = await api.delete(`/lookups/categories/${categoryId}`);
  return data;
};

export const updateUnit = async (unitId: number, data: { name?: string; abbreviation?: string }) => {
  const { data: result } = await api.put(`/lookups/units/${unitId}`, data);
  return result;
};

export const deleteUnit = async (unitId: number) => {
  const { data } = await api.delete(`/lookups/units/${unitId}`);
  return data;
};

export const deleteMR = async (mrId: number | string) => {
  const { data } = await api.delete(`/mrs/${mrId}`);
  return data;
};

export const deleteMRLine = async (mrId: number | string, lineId: number) => {
  const { data } = await api.delete(`/mrs/${mrId}/lines/${lineId}`);
  return data;
};

export type POLineMrAction = 'reorder' | 'not_needed';

// Cancellations queue (2026-09-16): one read model over whole-PO, PO-line and MR-line requests.
export interface CancellationRow {
  key: string;
  type: 'po' | 'po_line' | 'mr_line';
  status: 'Pending' | 'Approved' | 'Rejected';
  document: string;
  documentId: number;
  documentPath: string;
  lineId?: number;
  lineNo?: number;
  item: { code: string | null; description: string; descriptionAr: string | null };
  qty: number;
  ofQty: number;
  unit: string | null;
  received: number;
  value: number;
  department: { id: number; code: string; name: string } | null;
  vendor: { id: number; name: string } | null;
  requestedBy: string | null;
  requestedById: number | null;
  requestedAt: string | null;
  ageHours: number | null;
  reason: string | null;
  mrAction: POLineMrAction | null;
  mrRef: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  children?: CancellationRow[];
}
export interface CancellationQueueResult {
  status: 'pending' | 'decided';
  summary: { count: number; lines: number; value: number; oldestHours: number; byType: { po: number; po_line: number; mr_line: number } };
  rows: CancellationRow[];
  truncated: boolean;
  canDecide: boolean;
}
export const listCancellations = async (params?: { status?: 'pending' | 'decided'; type?: CancellationRow['type']; limit?: number; days?: number; departmentIds?: string }) => {
  const { data } = await api.get('/cancellations', { params });
  return data as CancellationQueueResult;
};

/** Ask to cancel part or all of a PO line's open balance; the CEO decides (2026-09-14). Returns the refreshed PO. */
export const requestPOLineCancel = async (
  poId: number | string,
  lineId: number,
  body: { qty: number; reason: string; mrAction: POLineMrAction },
) => {
  const { data } = await api.post(`/pos/${poId}/lines/${lineId}/cancel-request`, body);
  return data;
};

/** CEO / admin decision on a pending line cancellation. Returns the refreshed PO. */
export const decidePOLineCancel = async (
  poId: number | string,
  lineId: number,
  body: { decision: 'approve' | 'reject'; mrAction?: POLineMrAction },
) => {
  const { data } = await api.post(`/pos/${poId}/lines/${lineId}/cancel-decision`, body);
  return data;
};

export const deletePOLine = async (poId: number | string, lineId: number) => {
  const { data } = await api.delete(`/pos/${poId}/lines/${lineId}`);
  return data;
};

export const bulkUpdatePOStatus = async (
  poIds: number[],
  status: string,
  reason?: string
) => {
  const { data } = await api.put('/pos/bulk-status', { poIds, status, reason });
  return data;
};

export const deletePO = async (poId: number | string) => {
  const { data } = await api.delete(`/pos/${poId}`);
  return data;
};

// Goods Receiving Notes (GRN)
export const listGRNs = async (params?: {
  poId?: string;
  vendorId?: number;
  search?: string;
  vendorIds?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) => {
  const { data } = await api.get('/grns', { params });
  return data;
};

export const getGRN = async (grnId: string) => {
  const { data } = await api.get(`/grns/${grnId}`);
  return data;
};

export const createGRN = async (payload: {
  poId: string | number;
  deliveryNoteRef?: string;
  supplierInvoice?: string;
  receivedOn?: string;
  notes?: string;
  lines: Array<{
    poLineId: number;
    deliveredQty: number;
    acceptedQty: number;
    rejectedQty?: number;
    inspectionNotes?: string;
    rejectReason?: string;
    notes?: string;
  }>;
}) => {
  const { data } = await api.post('/grns', payload);
  return data;
};

export const createGRNReturn = async (grnLineId: number, payload: {
  returnQty: number;
  returnReason: string;
  notes?: string;
}) => {
  const { data } = await api.post(`/grns/lines/${grnLineId}/returns`, payload);
  return data;
};

export const updateGRNReturnStatus = async (returnId: number, payload: Record<string, any>) => {
  const { data } = await api.patch(`/grns/returns/${returnId}`, payload);
  return data;
};

export const deleteGRN = async (grnId: string) => {
  const { data } = await api.delete(`/grns/${grnId}`);
  return data;
};

// Parts Catalog
export const catalogList = async (params?: Record<string, any>) => {
  const { data } = await api.get('/catalog', { params });
  return data as { items: CatalogItem[]; total: number; page: number; pageSize: number };
};

export const catalogGet = async (id: number) => {
  const { data } = await api.get(`/catalog/${id}`);
  return data as CatalogItemDetail;
};

export const catalogCreate = async (payload: CatalogCreatePayload) => {
  const { data } = await api.post('/catalog', payload);
  return data as CatalogItem;
};

export const catalogUpdate = async (id: number, payload: CatalogUpdatePayload) => {
  const { data } = await api.patch(`/catalog/${id}`, payload);
  return data as CatalogItem;
};

export const catalogDelete = async (id: number) => {
  const { data } = await api.delete(`/catalog/${id}`);
  return data as CatalogItem;
};

export const catalogSearch = async (q: string, limit?: number) => {
  const { data } = await api.get('/catalog/search', { params: { q, limit } });
  return data as CatalogSearchResult[];
};

export const catalogBulkCreate = async (items: Array<{
  partNumber: string;
  description: string;
  defaultUnitPrice?: number;
  unitName?: string;
  categoryName?: string;
  vendorName?: string;
  currencyCode?: string;
  sku?: string;
  barcode?: string;
}>) => {
  const { data } = await api.post('/catalog/bulk', { items });
  return data as { created: CatalogItem[]; errors: Array<{ row: number; partNumber: string; error: string }>; totalProcessed: number };
};

// Catalog browser (MR create modal): paged, normalised search (Arabic letter shapes, digits, hyphens ignored).
export interface CatalogBrowseResult {
  items: CatalogSearchResult[];
  total: number;
  page: number;
  pageSize: number;
}
export const catalogBrowse = async (params: { q?: string; class?: string; page?: number; pageSize?: number }) => {
  const { data } = await api.get('/catalog/browse', { params });
  return data as CatalogBrowseResult;
};

// Admin: department settings (requesters may enter an estimated unit price)
export const updateDepartmentSettings = async (id: number, payload: { requesterEntersPrice?: boolean }) => {
  const { data } = await api.patch(`/lookups/departments/${id}`, payload);
  return data;
};

// Price engine (2026-09-06)
export interface ItemVendorPrice { vendorId: number; vendorName: string; appCode: string | null; preferred: boolean; listPrice: number | null; listDate: string | null; currency: string | null; leadTimeDays: number | null; vendorPartNo: string | null; vendorColour: string | null; lastPaid: number | null; lastPo: string | null; lastPoDate: string | null; driftPct: number | null; current: number | null }
export interface ItemPrices { itemId: number; partNumber: string; unit: { id: number; name: string } | null; current: { price: number | null; vendorId: number | null; vendorName: string | null; rule: string; date: string | null }; vendors: ItemVendorPrice[]; timeline: Array<{ date: string; source: string; price: number; vendor: string; ref: string | null; by: string | null }>; altUnits: Array<{ id: number; name: string; factor: number }>; deptPreferences: DeptPreference[] }
export interface DeptPreference { departmentId: number; departmentCode: string; departmentName: string; vendorId: number; vendorName: string; appCode: string | null; reason: string | null; setBy: string | null; setAt: string }
export const catalogDeptVendors = (id: number) => api.get(`/catalog/${id}/dept-vendors`).then((r) => r.data as DeptPreference[]);
export const setCatalogDeptVendor = (id: number, departmentId: number, payload: { vendorId: number; reason?: string }) => api.put(`/catalog/${id}/dept-vendors/${departmentId}`, payload).then((r) => r.data as DeptPreference[]);
export const removeCatalogDeptVendor = (id: number, departmentId: number) => api.delete(`/catalog/${id}/dept-vendors/${departmentId}`).then((r) => r.data as DeptPreference[]);
export const catalogItemPrices = (id: number) => api.get(`/catalog/${id}/prices`).then((r) => r.data as ItemPrices);
export const catalogPricesBatch = (ids: number[]) => api.get('/catalog/prices', { params: { ids: ids.join(',') } }).then((r) => r.data as Record<number, ItemPrices>);

// Item Card (2026-09-09): everything connected to one catalog item in one call.
export interface CatalogCardRequest { mrLineId: number; mrHeaderId: number; mrId: string; lineNo: number; departmentId: number | null; department: string | null; requester: string | null; requestDate: string | null; neededBy: string | null; urgency: string | null; mrStatus: string | null; qty: number | null; unit: string | null; unitPrice: number | null; currency: string | null; lineStatus: string | null; approvalStatus: string | null; vendorId: number | null; vendor: string | null; vendorSource: string | null; costCenterCode: string | null; costCenterName: string | null; lineNotes: string | null; poLineId: number | null; poHeaderId: number | null; poId: string | null }
export interface CatalogCardOrder { poLineId: number; poHeaderId: number; poId: string; poDate: string | null; status: string | null; vendorId: number | null; vendor: string | null; appCode: string | null; qty: number | null; unitPrice: number | null; actualUnitPrice: number | null; pricePaid: number | null; lineTotal: number | null; actualLineTotal: number | null; currency: string | null; totalReceived: number; returnedQty: number; mrHeaderId: number; mrId: string; department: string | null; createdBy: string | null; costCenterCode: string | null; open: boolean }
export interface CatalogCardReturn { id: number; qty: number | null; date: string | null; reason: string | null; status: string | null; creditNoteRef: string | null; refundAmount: number | null; returnedBy: string | null }
export interface CatalogCardReceipt { grnLineId: number; grnHeaderId: number; grnId: string; receivedOn: string | null; poHeaderId: number; poId: string; vendor: string | null; deliveredQty: number; acceptedQty: number; rejectedQty: number; inspectionStatus: string | null; rejectReason: string | null; receivedBy: string | null; deliveryNoteRef: string | null; supplierInvoice: string | null; returns: CatalogCardReturn[] }
export interface CatalogCardVendor { vendorId: number; name: string; appCode: string | null; preferred: boolean; poCount: number; orderedQty: number; receivedQty: number; returnedQty: number; acceptedQty: number; rejectedQty: number; grnPassed: number; grnFailed: number; spendAccepted: number | null; avgPrice: number | null; firstPo: string | null; firstPoId: string; lastPo: string | null; lastPoId: string; openLines: number }
export interface CatalogCardCostCenter { code: string | null; name: string | null; lines: number; qty: number; mrCount: number; value: number | null }
export interface CatalogCard {
  item: { id: number; partNumber: string; description: string; descriptionAr: string | null; sku: string | null; barcode: string | null; isActive: boolean; defaultUnitPrice: number | null; currency: string | null; unitId: number | null; unit: string | null; unitSet: boolean; unitFromLines: string | null; categoryId: number | null; category: string | null; preferredVendor: { id: number; name: string; appCode: string | null } | null; leadTimeDays: number | null; minStockLevel: number | null; maxStockLevel: number | null; reorderPoint: number | null; allowedUnitCategories: string | null; createdAt: string | null; createdBy: string | null; updatedAt: string | null; updatedBy: string | null };
  scope: { restricted: boolean; showMoney: boolean; departmentIds: number[] };
  stats: { requestLines: number; rejectedLines: number; requestedQty: number; approvedQty: number; departments: string[]; poCount: number; poLines: number; openPoCount: number; outstandingQty: number; vendorCount: number; orderedQty: number; receivedQty: number; acceptedQty: number; rejectedQty: number; returnedQty: number; grnCount: number; failedInspections: number; spendAccepted: number | null; orderedValue: number | null; avgPricePaid: number | null; lastPurchase: { poId: string; poHeaderId: number; poDate: string | null; qty: number | null; vendor: string | null; unitPrice: number | null } | null; firstPurchaseDate: string | null; currency: string };
  requests: CatalogCardRequest[]; orders: CatalogCardOrder[]; receipts: CatalogCardReceipt[]; vendors: CatalogCardVendor[]; costCenters: CatalogCardCostCenter[];
  audit: Array<{ at: string | null; who: string | null; what: string; detail: string | null; kind: 'record' | 'price' }>;
}
export const catalogCard = (id: number) => api.get(`/catalog/${id}/card`).then((r) => r.data as CatalogCard);

// Composer ↔ ERP sync (2026-09-09): pushes from the Item Composer desktop app are staged for review here.
export interface ComposerSyncBucketRow { code: string; cls: string | null; type: string | null; descEn: string; descAr: string; department: string | null; note: string | null; bucket: 'added' | 'changed' | 'removed' | 'dupSkipped' | 'shadeConflict'; changes?: string[]; current?: { id: number; description: string; descriptionAr: string | null; isActive: boolean } | null; conflictWith?: string | null; reactivation?: boolean; vendorRefs?: number; /** sha256s, present only when the picture differs (2026-09-22) */ picture?: { from: string | null; to: string | null } }
export interface ComposerSyncSummary { added: number; changed: number; removed: number; dupSkipped: number; shadeConflict: number; pictures?: number; vocab?: { dictValues: number; dictTypes: number; dictRetire: number; shades: number; motifs: number; departments: number; uoms: number } }
export interface ComposerSyncBatch { id: number; direction: 'push' | 'pull'; status: string; enginePin: string | null; dictHash: string | null; fullSnapshot: boolean; itemCount: number; summary: ComposerSyncSummary | null; sourceIp: string | null; receivedAt: string; reviewedBy: string | null; reviewedAt: string | null; report: any | null }
export interface ComposerSyncBatchDetail extends ComposerSyncBatch { rows: ComposerSyncBucketRow[] }
export const composerSyncBatches = () => api.get('/items/sync/batches').then((r) => r.data as ComposerSyncBatch[]);
export const composerSyncBatch = (id: number) => api.get(`/items/sync/batches/${id}`).then((r) => r.data as ComposerSyncBatchDetail);
export const composerSyncApply = (id: number, accept: { acceptAll?: boolean; acceptCodes?: string[]; removeCodes?: string[] }) => api.post(`/items/sync/batches/${id}/apply`, accept).then((r) => r.data as { report: any; batch: ComposerSyncBatch });
export const composerSyncReject = (id: number) => api.post(`/items/sync/batches/${id}/reject`).then((r) => r.data as ComposerSyncBatch);
export const composerSyncStatus = () => api.get('/items/sync/status').then((r) => r.data as { tokenConfigured: boolean; lastPush: string | null; lastPull: string | null; staged: number; itemsWithComposerData: number; enginePin: string });

// Item pictures (2026-09-22): set in the composer, stored in Spaces, read here through short-lived
// presigned URLs. The ERP never uploads or edits one.
export interface CatalogPicture {
  sha256: string;
  mime: string;
  size: number | null;
  /** Presigned GET, valid for 15 minutes. */
  url: string;
  /** Optional: when the composer last set it, if the server reports it. */
  updatedAt?: string | null;
}
/** Only items that currently have a picture come back. Max 1000 ids per call. */
export const catalogPictures = (ids: number[]) =>
  api.get('/catalog/pictures', { params: { ids: ids.join(',') } }).then((r) => r.data as Record<number, CatalogPicture>);
/** Review thumbnails: the server proxies the bytes for either side of the change (`to` = pushed, `from` = what it replaces). */
export const composerSyncStagedPictureUrl = async (batchId: number, code: string, which: 'to' | 'from' = 'to'): Promise<string> => {
  const { data } = await api.get(`/items/sync/batches/${batchId}/pictures/${encodeURIComponent(code)}`, { params: { which }, responseType: 'blob' });
  return URL.createObjectURL(data as Blob);
};

// Master Items sheet import (xlsx from the part-number app). Dry run unless commit.
export interface CatalogMasterImportResult {
  dryRun: boolean;
  fileName: string | null;
  totalRows: number;
  added: Array<{ code: string; description: string }>;
  updated: Array<{ code: string; changes: string[] }>;
  unchanged: number;
  skipped: Array<{ row: number; code: string; reason: string }>;
  warnings: Array<{ row: number; code: string; message: string }>;
  unmappedUnits: Record<string, number>;
  unmappedVendors: Record<string, number>;
  classCategoryMap: Record<string, string | null>;
  vendorPrices?: { rows: number; added: number; updated: number; defaultsChanged: number; vendorsCreated: string[]; unmappedVendorCodes: Record<string, number> };
  altUnits?: { rows: number; applied: number; unmappedUnits: string[] };
}
export const catalogImportMaster = async (file: File, commit: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(`/catalog/import/master${commit ? '?commit=1' : ''}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data as CatalogMasterImportResult;
};

// Catalog shared types (exported for use in components)
export const refineMRLine = async (lineId: number, data: Record<string, any>) => {
  const { data: result } = await api.patch(`/mrs/lines/${lineId}/refine`, data);
  return result;
};

export interface CatalogItem {
  id: number;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  defaultUnitPrice: number | null;
  sku: string | null;
  barcode: string | null;
  active: boolean;
  isActive?: boolean; // server field name; `active` kept for older callers
  allowedUnitCategories?: string | null;
  unit: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  preferredVendor: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
  // Inventory Phase 1.6: structured part-number segments
  classCode?: string | null;
  itemCode?: string | null;
  colorCode?: string | null;
  colorVariant?: string | null;
  sizeCode?: string | null;
  softnessCode?: string | null;
  supplierCode?: string | null;
}

export interface CatalogPriceHistory {
  id: number;
  changedAt: string;
  oldPrice: number | null;
  newPrice: number | null;
  changedBy: { id: number; displayName: string } | null;
  reason: string | null;
}

export interface CatalogItemDetail extends CatalogItem {
  priceHistory: CatalogPriceHistory[];
}

export interface CatalogSearchResult {
  id: number;
  partNumber: string;
  description: string;
  descriptionAr?: string | null;
  defaultUnitPrice: number | null;
  allowedUnitCategories?: string | null;
  unit: { id: number; name: string } | null;
  category: { id: number; name: string } | null;
  preferredVendor: { id: number; name: string } | null;
  currency: { id: number; code: string } | null;
}

export interface CatalogCreatePayload {
  partNumber: string;
  description: string;
  defaultUnitPrice?: number | null;
  unitId?: number | null;
  categoryId?: number | null;
  preferredVendorId?: number | null;
  currencyId?: number | null;
  sku?: string | null;
  barcode?: string | null;
  allowedUnitCategories?: string | null;
  // Inventory Phase 1.6: structured part-number segments
  classCode?: string | null;
  itemCode?: string | null;
  colorCode?: string | null;
  colorVariant?: string | null;
  sizeCode?: string | null;
  softnessCode?: string | null;
  supplierCode?: string | null;
}

export interface CatalogUpdatePayload extends Partial<CatalogCreatePayload> {
  priceChangeReason?: string;
}

// =====================================================================
// CRM — Clients
// =====================================================================

export const crmListClients = async (params?: {
  page?: number; limit?: number; search?: string; accountManagerId?: number; lifecycleStage?: string;
}) => {
  const { data } = await api.get('/crm/clients', { params });
  return data;
};

export const crmGetClient = async (id: number) => {
  const { data } = await api.get(`/crm/clients/${id}`);
  return data;
};

export const crmCreateClient = async (clientData: Record<string, any>) => {
  const { data } = await api.post('/crm/clients', clientData);
  return data;
};

export const crmUpdateClient = async (id: number, clientData: Record<string, any>) => {
  const { data } = await api.put(`/crm/clients/${id}`, clientData);
  return data;
};

export const crmCreateLead = async (leadData: Record<string, any>) => {
  const { data } = await api.post('/crm/leads', leadData);
  return data;
};

// =====================================================================
// CRM — Deals
// =====================================================================

export const crmListDeals = async (params?: {
  page?: number; limit?: number; stage?: string; assignedToId?: number; clientId?: number; priority?: string; search?: string;
}) => {
  const { data } = await api.get('/crm/deals', { params });
  return data;
};

export const crmGetDeal = async (id: number) => {
  const { data } = await api.get(`/crm/deals/${id}`);
  return data;
};

export const crmCreateDeal = async (dealData: Record<string, any>) => {
  const { data } = await api.post('/crm/deals', dealData);
  return data;
};

export const crmUpdateDeal = async (id: number, dealData: Record<string, any>) => {
  const { data } = await api.put(`/crm/deals/${id}`, dealData);
  return data;
};

export const crmGetPipeline = async (assignedToId?: number) => {
  const { data } = await api.get('/crm/deals/pipeline', { params: assignedToId ? { assignedToId } : undefined });
  return data;
};

export const crmAdvanceDeal = async (id: number, stageData?: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${id}/advance`, { stageData });
  return data;
};

export const crmRevertDeal = async (id: number, targetStage: string) => {
  const { data } = await api.post(`/crm/deals/${id}/revert`, { targetStage });
  return data;
};

export const crmSetDealStage = async (id: number, stage: string, stageData?: Record<string, any>) => {
  const { data } = await api.put(`/crm/deals/${id}/stage`, { stage, stageData });
  return data;
};

export const crmValidateGate = async (id: number, targetStage: string) => {
  const { data } = await api.post(`/crm/deals/${id}/validate-gate`, { targetStage });
  return data;
};

// =====================================================================
// CRM — Deal Sub-resources
// =====================================================================

export const crmListDealItems = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/items`, { params });
  // P36: server returns { data, total }; unwrap for backward compatibility
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmAddDealItem = async (dealId: number, itemData: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/items`, itemData);
  return data;
};

export const crmUpdateDealItem = async (dealId: number, itemId: number, itemData: Record<string, any>) => {
  const { data } = await api.put(`/crm/deals/${dealId}/items/${itemId}`, itemData);
  return data;
};

export const crmDeleteDealItem = async (dealId: number, itemId: number) => {
  const { data } = await api.delete(`/crm/deals/${dealId}/items/${itemId}`);
  return data;
};

export const crmUploadItemFile = async (dealId: number, itemId: number, file: File, fileType: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileType', fileType);
  const res = await api.post(`/crm/deals/${dealId}/items/${itemId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const crmListItemFiles = async (dealId: number, itemId: number) => {
  const res = await api.get(`/crm/deals/${dealId}/items/${itemId}/files`);
  return res.data;
};

export const crmDeleteItemFile = async (dealId: number, itemId: number, fileId: number) => {
  const res = await api.delete(`/crm/deals/${dealId}/items/${itemId}/files/${fileId}`);
  return res.data;
};

export const crmGetLatestRates = async () => {
  const res = await api.get('/crm/currencies/rates/latest');
  return res.data;
};

export const crmGetExchangeRate = async () => {
  const { data } = await api.get('/crm/exchange-rate');
  return data;
};

export const crmFetchRates = async () => {
  const res = await api.post('/crm/currencies/rates/fetch');
  return res.data;
};

export const crmListPayments = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/payments`, { params });
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmAddPayment = async (dealId: number, paymentData: Record<string, any> | FormData) => {
  const isFormData = paymentData instanceof FormData;
  const { data } = await api.post(`/crm/deals/${dealId}/payments`, paymentData, {
    headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
  });
  return data;
};

// Download payment proof as blob (the proof endpoint requires JWT — plain
// <a href> in a new tab strips the Authorization header and the browser shows
// `{"error":"No token provided"}`, so we fetch via axios and open a blob URL).
// We rewrap the response into a new Blob with the server's Content-Type so the
// blob URL renders inline in the browser instead of being sniffed as XML.
export const crmDownloadPaymentProof = async (dealId: number, paymentId: number) => {
  const response = await api.get(`/crm/deals/${dealId}/payments/${paymentId}/proof`, {
    responseType: 'blob',
  });
  const contentType = (response.headers?.['content-type'] as string | undefined) || 'application/octet-stream';
  return new Blob([response.data], { type: contentType });
};

export const crmListQuotations = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/quotations`, { params });
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export interface CreateQuotationPayload {
  quotationType?: 'original' | 'supplementary';
  currency: 'EGP' | 'USD';
  title?: string;
  selectedItemIds: number[];
  priceOverrides?: Record<string, number>;
  discount?: number;
  discountType?: 'flat' | 'percentage';
  tax?: number;
  validUntil?: string;
  termsAndConditions?: string;
  designDirection?: string;
  internalNotes?: string;
  paymentSchedule?: '50/50' | '40/30/30' | '100_upfront' | 'custom';
  paymentMilestones?: { name: string; percentage: number }[];
}

export const crmCreateQuotation = async (dealId: number, body: CreateQuotationPayload) => {
  const { data } = await api.post(`/crm/deals/${dealId}/quotations`, body);
  return data;
};

export const crmUpdateQuotation = async (
  dealId: number,
  quotationId: number,
  body: Omit<CreateQuotationPayload, 'quotationType'>,
) => {
  const { data } = await api.put(`/crm/deals/${dealId}/quotations/${quotationId}`, body);
  return data;
};

export const crmMarkQuotationSent = async (dealId: number, quotationId: number, sentMethod: string) => {
  const { data } = await api.put(`/crm/deals/${dealId}/quotations/${quotationId}/send`, { sentMethod });
  return data;
};

export const crmApproveQuotation = async (dealId: number, quotationId: number) => {
  const { data } = await api.put(`/crm/deals/${dealId}/quotations/${quotationId}/approve`);
  return data;
};

export const crmMarkQuotationClientResponse = async (quotationId: number, clientStatus: string) => {
  const { data } = await api.put(`/crm/quotations/${quotationId}/client-response`, { status: clientStatus });
  return data;
};

export const crmRequestQuotationReopen = async (dealId: number, quotationId: number, reason: string) => {
  const { data } = await api.post(`/crm/deals/${dealId}/quotations/${quotationId}/reopen-request`, { reason });
  return data;
};

export const crmResolveQuotationReopen = async (requestId: number, approved: boolean, reviewNote?: string) => {
  const { data } = await api.put(`/crm/quotation-reopen-requests/${requestId}`, { approved, reviewNote });
  return data;
};

export const crmUploadQuotationImage = async (quotationId: number, file: File, itemIndex: number) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('itemIndex', String(itemIndex));
  const { data } = await api.post(`/crm/quotations/${quotationId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const crmDeleteQuotationImage = async (quotationId: number, imageId: number) => {
  const { data } = await api.delete(`/crm/quotations/${quotationId}/images/${imageId}`);
  return data;
};

export const crmListQuotationImages = async (quotationId: number, opts?: { base64?: boolean }) => {
  const params = opts?.base64 ? '?base64=1' : '';
  const { data } = await api.get(`/crm/quotations/${quotationId}/images${params}`);
  return data;
};

// ─── Invoices ───
export const crmGenerateInvoices = async (dealId: number, quotationId: number) => {
  const { data } = await api.post(`/crm/deals/${dealId}/invoices/generate`, { quotationId });
  return data;
};

export const crmListInvoices = async (dealId: number) => {
  const { data } = await api.get(`/crm/deals/${dealId}/invoices`);
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmMarkInvoiceSent = async (invoiceId: number, sentMethod: string) => {
  const { data } = await api.put(`/crm/invoices/${invoiceId}/send`, { sentMethod });
  return data;
};

export const crmUpdateInvoice = async (invoiceId: number, body: { dueDate?: string; paymentInstructions?: string; notes?: string }) => {
  const { data } = await api.put(`/crm/invoices/${invoiceId}`, body);
  return data;
};

export const crmCancelInvoice = async (invoiceId: number) => {
  const { data } = await api.put(`/crm/invoices/${invoiceId}/cancel`);
  return data;
};

export const crmListSketches = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/sketches`, { params });
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmAddSketch = async (dealId: number, sketchData: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/sketches`, sketchData);
  return data;
};

export const crmRequestSketchReopen = async (dealId: number, sketchId: number, reason: string) => {
  const { data } = await api.post(`/crm/deals/${dealId}/sketches/${sketchId}/reopen-request`, { reason });
  return data;
};

export const crmResolveSketchReopen = async (requestId: number, approved: boolean, reviewNote?: string) => {
  const { data } = await api.put(`/crm/sketch-reopen-requests/${requestId}`, { approved, reviewNote });
  return data;
};

// Sample Category Review API
export const crmSetRequiredSampleCategories = async (dealId: number, categories: string[]) => {
  const { data } = await api.put(`/crm/deals/${dealId}/required-sample-categories`, { categories });
  return data;
};

export const crmAddCategorySample = async (dealId: number, payload: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/sample-categories`, payload);
  return data;
};

export const crmRecordCategoryFeedback = async (dealId: number, reviewId: number, payload: Record<string, any>) => {
  const { data } = await api.put(`/crm/deals/${dealId}/sample-categories/${reviewId}/feedback`, payload);
  return data;
};

export const crmRequestCategoryReopen = async (dealId: number, reviewId: number, reason: string) => {
  const { data } = await api.post(`/crm/deals/${dealId}/sample-categories/${reviewId}/reopen`, { reason });
  return data;
};

export const crmResolveCategoryReopen = async (requestId: number, approved: boolean, reviewNote?: string) => {
  const { data } = await api.put(`/crm/sample-category-reopens/${requestId}`, { approved, reviewNote });
  return data;
};

export const crmListSamples = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/samples`, { params });
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmAddSample = async (dealId: number, sampleData: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/samples`, sampleData);
  return data;
};

// --- CRM Job Order types (Step B: structured lines) -------------------
export interface CrmJobOrderLineInput {
  sortOrder?: number;
  sampleRequestId?: number | null;
  catalogItemId?: number | null;
  embroiderySampleCode?: string | null;
  category?: string | null;
  partNumber?: string | null;
  description: string;
  qty: number;
  unitId?: number | null;
  notes?: string | null;
}

export interface CrmJobOrderCreatePayload {
  materials?: any;
  notes?: string;
  status?: 'draft' | 'active';
  lines?: CrmJobOrderLineInput[];
}

export interface CrmJobOrderUpdatePayload {
  status?: 'draft' | 'active' | 'completed';
  materials?: any;
  notes?: string;
  lines?: CrmJobOrderLineInput[];
}

export const crmListJobOrders = async (dealId: number) => {
  const { data } = await api.get(`/crm/deals/${dealId}/job-orders`);
  return data;
};

export const crmGetJobOrder = async (dealId: number, jobOrderId: number) => {
  const { data } = await api.get(`/crm/deals/${dealId}/job-orders/${jobOrderId}`);
  return data;
};

export const crmCreateJobOrder = async (dealId: number, jobOrderData: CrmJobOrderCreatePayload) => {
  const { data } = await api.post(`/crm/deals/${dealId}/job-orders`, jobOrderData);
  return data;
};

export const crmUpdateJobOrder = async (
  dealId: number,
  jobOrderId: number,
  updateData: CrmJobOrderUpdatePayload,
) => {
  const { data } = await api.put(`/crm/deals/${dealId}/job-orders/${jobOrderId}`, updateData);
  return data;
};

export const crmGenerateMrFromJobOrder = async (
  dealId: number,
  jobOrderId: number,
  payload: { departmentId: number; neededBy?: string | null; urgency?: string | null; notes?: string | null },
): Promise<{ mrId: string; mrHeaderId: number }> => {
  const { data } = await api.post(
    `/crm/deals/${dealId}/job-orders/${jobOrderId}/generate-mr`,
    payload,
  );
  return data;
};

export const crmListFittings = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/fittings`, { params });
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmAddFitting = async (dealId: number, fittingData: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/fittings`, fittingData);
  return data;
};

export const crmUpdateFitting = async (dealId: number, fittingId: number, fittingData: Record<string, any>) => {
  const { data } = await api.put(`/crm/deals/${dealId}/fittings/${fittingId}`, fittingData);
  return data;
};

export const crmListFollowUps = async (dealId: number, params?: { page?: number; limit?: number }) => {
  const { data } = await api.get(`/crm/deals/${dealId}/follow-ups`, { params });
  return Array.isArray(data) ? data : (data?.data ?? data);
};

export const crmAddFollowUp = async (dealId: number, followUpData: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/follow-ups`, followUpData);
  return data;
};

export const crmCompleteFollowUp = async (dealId: number, followUpId: number, notes?: string) => {
  const { data } = await api.put(`/crm/deals/${dealId}/follow-ups/${followUpId}/complete`, { notes });
  return data;
};

export const crmListActivities = async (dealId: number, type?: string) => {
  const { data } = await api.get(`/crm/deals/${dealId}/activities`, { params: type ? { type } : undefined });
  return data;
};

export const crmAddActivity = async (dealId: number, activityData: Record<string, any>) => {
  const { data } = await api.post(`/crm/deals/${dealId}/activities`, activityData);
  return data;
};

// =====================================================================
// CRM — Tasks
// =====================================================================

export const crmListTasks = async (params?: {
  assignedToId?: number; status?: string; dealId?: number; priority?: string; queueName?: string;
}) => {
  const { data } = await api.get('/crm/tasks', { params });
  return data;
};

export const crmCreateTask = async (taskData: Record<string, any>) => {
  const { data } = await api.post('/crm/tasks', taskData);
  return data;
};

export const crmUpdateTask = async (id: number, taskData: Record<string, any>) => {
  const { data } = await api.put(`/crm/tasks/${id}`, taskData);
  return data;
};

export const crmCompleteTask = async (id: number) => {
  const { data } = await api.put(`/crm/tasks/${id}/complete`);
  return data;
};

// =====================================================================
// CRM — Follow-ups (cross-deal) & Dashboard
// =====================================================================

export const crmListAllFollowUps = async (params?: { pending?: boolean; overdue?: boolean }) => {
  const { data } = await api.get('/crm/follow-ups', { params });
  return data;
};

export const crmGetDashboard = async () => {
  const { data } = await api.get('/crm/dashboard');
  return data;
};

export const crmGetMyDashboard = async () => {
  const { data } = await api.get('/crm/dashboard/my');
  return data;
};

export const crmHoldDeal = (dealId: number, reason: string) =>
  api.post(`/crm/deals/${dealId}/hold`, { reason }).then(r => r.data);

export const crmResumeDeal = (dealId: number) =>
  api.post(`/crm/deals/${dealId}/resume`, {}).then(r => r.data);

export const crmLoseDeal = (dealId: number, reason: string) =>
  api.post(`/crm/deals/${dealId}/lose`, { reason }).then(r => r.data);

// Phase 5 — CEO Tools
export const crmBypassGate = (dealId: number, data: { gateNumber: number; requirement: string; reason: string }) =>
  api.post(`/crm/deals/${dealId}/bypass-gate`, data).then(r => r.data);

export const crmGetBlockedDeals = () =>
  api.get('/crm/deals/blocked').then(r => r.data);

export const crmGetBypassLog = () =>
  api.get('/crm/bypass-log').then(r => r.data);

// =====================================================================
// Audit Trail
// =====================================================================

export const getAuditTrail = (params?: Record<string, any>) =>
  api.get('/audit', { params }).then(r => r.data);

export const getEntityHistory = (entityType: string, entityId: string) =>
  api.get(`/audit/entity/${entityType}/${entityId}`).then(r => r.data);

export const getAuditEntityTypes = () =>
  api.get('/audit/entity-types').then(r => r.data);

// =====================================================================
// Vendor Management
// =====================================================================

export const vendorList = (params?: Record<string, any>) =>
  api.get('/vendors', { params }).then(r => r.data);

export interface VendorOverviewRow { id: number; appCode: string | null; nameAr: string | null; supplies: string[]; pricedItems: number; lastPoId: string | null; lastPoDate: string | null; spend12m: number; onTimePct: number | null; onTimeN: number; driftPct: number | null }
export const vendorOverview = () => api.get('/vendors/overview').then((r) => r.data as VendorOverviewRow[]);
export const vendorStatsExtended = () => api.get('/vendors/stats-extended').then((r) => r.data);
export const vendorDuplicates = () => api.get('/vendors/duplicates').then((r) => r.data as Array<{ key: string; vendors: Array<{ id: number; name: string; status: string; pos: number }> }>);
export const vendorPendingQueue = () => api.get('/vendors/pending-queue').then((r) => r.data as Array<{ id: number; name: string; createdAt: string; requestedFromMrId: string | null; mrLines: number; department: string | null; requester: string | null; matches: Array<{ id: number; name: string; appCode: string | null; status: string; score: number }> }>);
export const vendorMetrics = (id: number) => api.get(`/vendors/${id}/metrics`).then((r) => r.data);
export const vendorStats = () =>
  api.get('/vendors/stats').then(r => r.data);

export const vendorFilters = () =>
  api.get('/vendors/filters').then(r => r.data);

export const vendorGet = (id: number) =>
  api.get(`/vendors/${id}`).then(r => r.data);

export const vendorCreate = (data: Record<string, any>) =>
  api.post('/vendors', data).then(r => r.data);

export const vendorUpdate = (id: number, data: Record<string, any>) =>
  api.patch(`/vendors/${id}`, data).then(r => r.data);

export const vendorDeactivate = (id: number) =>
  api.delete(`/vendors/${id}`).then(r => r.data);

export const vendorSimilar = (name: string, excludeId?: number) =>
  api.get('/vendors/similar', { params: { name, excludeId } }).then(r => r.data);

export const vendorMergePreview = (targetId: number, sourceIds: number[]) =>
  api.post('/vendors/merge/preview', { targetId, sourceIds }).then(r => r.data);

export const vendorMerge = (targetId: number, sourceIds: number[]) =>
  api.post('/vendors/merge', { targetId, sourceIds }).then(r => r.data);

export const vendorContacts = (vendorId: number) =>
  api.get(`/vendors/${vendorId}/contacts`).then(r => r.data);

export const vendorContactCreate = (vendorId: number, data: Record<string, any>) =>
  api.post(`/vendors/${vendorId}/contacts`, data).then(r => r.data);

export const vendorContactUpdate = (vendorId: number, contactId: number, data: Record<string, any>) =>
  api.patch(`/vendors/${vendorId}/contacts/${contactId}`, data).then(r => r.data);

export const vendorContactDelete = (vendorId: number, contactId: number) =>
  api.delete(`/vendors/${vendorId}/contacts/${contactId}`).then(r => r.data);

export const vendorApprove = (id: number, updates?: Record<string, any>) =>
  api.post(`/vendors/${id}/approve`, { updates }).then(r => r.data);

export const vendorReject = (id: number, reason: string) =>
  api.post(`/vendors/${id}/reject`, { reason }).then(r => r.data);

// =====================================================================
// Purchase Returns
// =====================================================================

export const listReturns = (params?: Record<string, any>) =>
  api.get('/returns', { params }).then(r => r.data);

export const getReturnStats = () =>
  api.get('/returns/stats').then(r => r.data);

export const getReturnReasonCodes = () =>
  api.get('/returns/reason-codes').then(r => r.data);

export const updateReturn = (id: number, data: Record<string, any>) =>
  api.patch(`/returns/${id}`, data).then(r => r.data);

export const bulkSettleReturns = (data: Record<string, any>) =>
  api.post('/returns/bulk-settle', data).then(r => r.data);

export const getVendorReturns = (vendorId: number) =>
  api.get(`/returns/vendor/${vendorId}`).then(r => r.data);

// =====================================================================
// Client Samples
// =====================================================================

export const listClientSamples = async (params?: {
  status?: string;
  category?: string;
  search?: string;
  priority?: string;
  role?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) => {
  const { data } = await api.get('/client-samples', { params });
  return data;
};

export const getClientSample = async (id: number) => {
  const { data } = await api.get(`/client-samples/${id}`);
  return data;
};

export const getDealsNeedingSamples = async () => {
  const { data } = await api.get('/client-samples/by-deal/needs');
  return data;
};

export const createClientSampleMoodBoard = async (payload: {
  dealId: number;
  dealItemId?: number;
  category: string;
  title: string;
  description: string;
  specifications?: string;
  quantity?: string;
  referenceImages?: any[];
  designerNotes?: string;
  priority?: string;
}) => {
  const { data } = await api.post('/client-samples', payload);
  return data;
};

export const updateClientSample = async (id: number, payload: any) => {
  const { data } = await api.put(`/client-samples/${id}`, payload);
  return data;
};

export const submitSampleToProc = async (id: number) => {
  const { data } = await api.put(`/client-samples/${id}/submit`);
  return data;
};

export const startSamplePurchasing = async (id: number) => {
  const { data } = await api.put(`/client-samples/${id}/start-purchasing`);
  return data;
};

export const markSampleReceived = async (id: number, payload: {
  purchasedImages?: any[];
  vendorName?: string;
  actualCost?: number;
  procurementNotes?: string;
}) => {
  const { data } = await api.put(`/client-samples/${id}/mark-received`, payload);
  return data;
};

export const sampleDesignerReview = async (id: number, approved: boolean, note?: string) => {
  const { data } = await api.put(`/client-samples/${id}/designer-review`, { approved, note });
  return data;
};

export const sampleAmReview = async (id: number, approved: boolean, note?: string) => {
  const { data } = await api.put(`/client-samples/${id}/am-review`, { approved, note });
  return data;
};

export const scheduleSampleAppointment = async (id: number, date: string) => {
  const { data } = await api.put(`/client-samples/${id}/schedule-appointment`, { date });
  return data;
};

export const recordSampleClientFeedback = async (id: number, approved: boolean, feedback?: string) => {
  const { data } = await api.put(`/client-samples/${id}/client-feedback`, { approved, feedback });
  return data;
};

export const recordSampleClientConditional = async (id: number, conditionNotes: string) => {
  const { data } = await api.put(`/client-samples/${id}/client-conditional`, { conditionNotes });
  return data;
};

export const sampleScheduleAppointment = async (id: number, date: string) => {
  const { data } = await api.put(`/client-samples/${id}/schedule-appointment`, { date });
  return data;
};

export const sampleClientFeedback = async (id: number, approved: boolean, feedback?: string) => {
  const { data } = await api.put(`/client-samples/${id}/client-feedback`, { approved, feedback });
  return data;
};

export const linkSampleToMR = async (id: number, mrHeaderId: number) => {
  const { data } = await api.put(`/client-samples/${id}/link-mr`, { mrHeaderId });
  return data;
};

export const linkSampleToPO = async (id: number, poHeaderId: number) => {
  const { data } = await api.put(`/client-samples/${id}/link-po`, { poHeaderId });
  return data;
};

export const getSampleStatusForDeal = async (dealId: number) => {
  const { data } = await api.get(`/client-samples/by-deal/${dealId}`);
  return data;
};

export const getClientSamplesOverview = async (assignedToId?: number) => {
  const { data } = await api.get('/client-samples/overview-by-deal', { params: assignedToId ? { assignedToId } : undefined });
  return data;
};

// Lightweight lookups for Client Samples (no CRM permission needed)
export const lookupClientsForSamples = async (search?: string, limit?: number) => {
  const { data } = await api.get('/client-samples/lookup-clients', { params: { search, limit } });
  return data;
};

export const lookupDealsForSamples = async (search?: string, clientId?: number, limit?: number) => {
  const { data } = await api.get('/client-samples/lookup-deals', { params: { search, clientId, limit } });
  return data;
};

export const lookupDealItemsForSamples = async (dealId: number) => {
  const { data } = await api.get(`/client-samples/lookup-deal-items/${dealId}`);
  return data;
};

export const lookupMRsForSamples = (search?: string) =>
  api.get('/client-samples/lookup-mrs', { params: { search } }).then(r => r.data);

export const lookupPOsForSamples = (search?: string) =>
  api.get('/client-samples/lookup-pos', { params: { search } }).then(r => r.data);

export const uploadSampleMoodBoardFiles = async (sampleId: number, files: File[]) => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  const { data } = await api.post(`/client-samples/${sampleId}/upload-mood-board`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const uploadSamplePurchasedFiles = async (sampleId: number, files: File[]) => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  const { data } = await api.post(`/client-samples/${sampleId}/upload-purchased`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// Assign catalog item to fabric sample
export const csAssignPart = async (id: number, catalogItemId: number) => {
  const { data } = await api.put(`/client-samples/${id}/assign-part`, { catalogItemId });
  return data;
};

// Auto-generate embroidery sample code
export const csAssignEmbroideryCode = async (id: number) => {
  const { data } = await api.put(`/client-samples/${id}/assign-embroidery-code`);
  return data;
};

// Get fabric samples blocking job order
export const csGetBlockingFabric = async (dealId: number) => {
  const { data } = await api.get(`/client-samples/blocking-fabric/${dealId}`);
  return data;
};

// Get approved samples with catalog items for job order suggestions
export const csGetSuggestedParts = async (dealId: number) => {
  const { data } = await api.get(`/client-samples/suggested-parts/${dealId}`);
  return data;
};

// Get CS procurement status for CRM Samples tab
export const csGetCrmStatus = async (dealId: number) => {
  const { data } = await api.get(`/client-samples/crm-status/${dealId}`);
  return data;
};

// Phase 8B: Client Samples Enhancements
export const csAssignDesignCode = (id: number) =>
  api.put(`/client-samples/${id}/assign-design-code`).then(r => r.data);

export const csApproveConditions = (id: number) =>
  api.put(`/client-samples/${id}/approve-conditions`).then(r => r.data);

export const csDenyConditions = (id: number, denialNote: string) =>
  api.put(`/client-samples/${id}/deny-conditions`, { denialNote }).then(r => r.data);

export const csRequestBiggerSample = (id: number) =>
  api.post(`/client-samples/${id}/request-bigger-sample`).then(r => r.data);

export const csCreateMR = (id: number, mrData: any) =>
  api.post(`/client-samples/${id}/create-mr`, mrData).then(r => r.data);

export const csCreateAndAssignPart = (id: number, data: { partNumber: string; description: string }) =>
  api.post(`/client-samples/${id}/create-and-assign-part`, data).then(r => r.data);

export const getRequiredSamplesQueue = () =>
  api.get('/client-samples/required-samples').then(r => r.data);

export const crmAmReviewFromCrm = (dealId: number, categoryReviewId: number) =>
  api.put(`/crm/deals/${dealId}/sample-categories/${categoryReviewId}/am-review`).then(r => r.data);

export const crmConditionallyApprove = (dealId: number, categoryReviewId: number, conditionNotes: string) =>
  api.put(`/crm/deals/${dealId}/sample-categories/${categoryReviewId}/feedback`, { conditionalApproval: true, conditionNotes }).then(r => r.data);

// CRM: Reject a sample category (triggers new CS version)
export const crmRejectCategorySample = async (dealId: number, reviewId: number, clientFeedback: string) => {
  const { data } = await api.put(`/crm/deals/${dealId}/sample-categories/${reviewId}/reject`, { clientFeedback });
  return data;
};

// ═══════════════════════════════════════════════════════════════════════════════
// PIPELINE STAGES (dynamic)
// ═══════════════════════════════════════════════════════════════════════════════

export const crmGetPipelineStages = () => api.get('/crm/pipeline-stages').then(r => r.data);
export const crmCreatePipelineStage = (data: any) => api.post('/crm/pipeline-stages', data).then(r => r.data);
export const crmUpdatePipelineStage = (id: number, data: any) => api.put(`/crm/pipeline-stages/${id}`, data).then(r => r.data);
export const crmReorderPipelineStages = (stageIds: number[]) => api.put('/crm/pipeline-stages/reorder', { stageIds }).then(r => r.data);
export const crmDisablePipelineStage = (id: number) => api.delete(`/crm/pipeline-stages/${id}`).then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// GATE RULES (dynamic)
// ═══════════════════════════════════════════════════════════════════════════════

export const crmGetGateRules = (stageId: number) => api.get(`/crm/gate-rules/${stageId}`).then(r => r.data);
export const crmCreateGateRule = (data: any) => api.post('/crm/gate-rules', data).then(r => r.data);
export const crmUpdateGateRule = (id: number, data: any) => api.put(`/crm/gate-rules/${id}`, data).then(r => r.data);
export const crmReorderGateRules = (ruleIds: number[]) => api.put('/crm/gate-rules/reorder', { ruleIds }).then(r => r.data);
export const crmDeleteGateRule = (id: number) => api.delete(`/crm/gate-rules/${id}`).then(r => r.data);
export const crmGetBuiltinEvaluators = () => api.get('/crm/gate-rules/builtin-evaluators').then(r => r.data);
export const crmGetFieldOptions = () => api.get('/crm/gate-rules/field-options').then(r => r.data);
export const crmGetRelationOptions = () => api.get('/crm/gate-rules/relation-options').then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// APPOINTMENTS
// ═══════════════════════════════════════════════════════════════════════════════

export const crmListAppointments = (params?: any) => api.get('/crm/appointments', { params }).then(r => r.data);
export const crmGetAppointment = (id: number) => api.get(`/crm/appointments/${id}`).then(r => r.data);
export const crmCreateAppointment = (data: any) => api.post('/crm/appointments', data).then(r => r.data);
export const crmUpdateAppointment = (id: number, data: any) => api.put(`/crm/appointments/${id}`, data).then(r => r.data);
export const crmConfirmAppointment = (id: number) => api.post(`/crm/appointments/${id}/confirm`).then(r => r.data);
export const crmCompleteAppointment = (id: number, data?: any) => api.post(`/crm/appointments/${id}/complete`, data || {}).then(r => r.data);
export const crmCancelAppointment = (id: number, reason: string) => api.post(`/crm/appointments/${id}/cancel`, { reason }).then(r => r.data);
export const crmRescheduleAppointment = (id: number, data: any) => api.post(`/crm/appointments/${id}/reschedule`, data).then(r => r.data);
export const crmGetCalendarData = (params: any) => api.get('/crm/appointments/calendar', { params }).then(r => r.data);
export const crmGetAppointmentTypes = () => api.get('/crm/appointments/types').then(r => r.data);
export const crmGetDealAppointments = (dealId: number) => api.get(`/crm/deals/${dealId}/appointments`).then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// FORM FIELDS (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const crmListFormFields = (params?: { scope?: string; category?: string }) => api.get('/crm/form-fields', { params }).then(r => r.data);
export const crmGetUnifiedFields = (category: string) => api.get(`/crm/form-fields/unified/${category}`).then(r => r.data);
export const crmCreateFormField = (data: any) => api.post('/crm/form-fields', data).then(r => r.data);
export const crmUpdateFormField = (id: number, data: any) => api.put(`/crm/form-fields/${id}`, data).then(r => r.data);
export const crmDeleteFormField = (id: number) => api.delete(`/crm/form-fields/${id}`).then(r => r.data);

// Form Fields — Form Builder
export const crmGetFieldsByForm = (formKey: string, includeHidden = false, category?: string) => {
  const params: any = {};
  if (includeHidden) params.includeHidden = 'true';
  if (category) params.category = category;
  return api.get(`/crm/form-fields/by-form/${formKey}`, { params }).then(r => r.data);
};
export const crmReorderFields = (updates: { id: number; sortOrder: number; section?: string }[]) => api.patch('/crm/form-fields/reorder', { updates }).then(r => r.data);

// Form Sections
export const crmListFormSections = (formKey?: string) => api.get('/crm/form-sections', { params: formKey ? { formKey } : {} }).then(r => r.data);
export const crmCreateFormSection = (data: any) => api.post('/crm/form-sections', data).then(r => r.data);
export const crmUpdateFormSection = (id: number, data: any) => api.put(`/crm/form-sections/${id}`, data).then(r => r.data);
export const crmDeleteFormSection = (id: number) => api.delete(`/crm/form-sections/${id}`).then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ITEM CATEGORIES (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const crmListItemCategories = () => api.get('/crm/item-categories').then(r => r.data);
export const crmCreateItemCategory = (data: any) => api.post('/crm/item-categories', data).then(r => r.data);
export const crmUpdateItemCategory = (id: number, data: any) => api.put(`/crm/item-categories/${id}`, data).then(r => r.data);
export const crmDeleteItemCategory = (id: number) => api.delete(`/crm/item-categories/${id}`).then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// DROPDOWN OPTIONS (admin)
// ═══════════════════════════════════════════════════════════════════════════════

export const crmListDropdownOptions = (dropdownKey: string) => api.get('/crm/dropdown-options', { params: { dropdown_key: dropdownKey } }).then(r => r.data);
export const crmCreateDropdownOption = (data: any) => api.post('/crm/dropdown-options', data).then(r => r.data);
export const crmUpdateDropdownOption = (id: number, data: any) => api.put(`/crm/dropdown-options/${id}`, data).then(r => r.data);
export const crmDeleteDropdownOption = (id: number) => api.delete(`/crm/dropdown-options/${id}`).then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// DESIGN BRIEF
// ═══════════════════════════════════════════════════════════════════════════════

export const crmGetDesignBrief = (dealId: number) => api.get(`/crm/deals/${dealId}/design-brief`).then(r => r.data);
export const crmSaveDesignBrief = (dealId: number, data: any) => api.post(`/crm/deals/${dealId}/design-brief`, data).then(r => r.data);
export const crmUploadDesignRef = (dealId: number, itemIndex: string, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post(`/crm/deals/${dealId}/design-refs/${itemIndex}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};
export const crmDeleteDesignRef = (dealId: number, itemIndex: string, fileName: string) => api.delete(`/crm/deals/${dealId}/design-refs/${itemIndex}/${fileName}`).then(r => r.data);
export const crmGetDesignRefs = (dealId: number, itemIndex: string) => api.get(`/crm/deals/${dealId}/design-refs/${itemIndex}`).then(r => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// NOTIFICATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const getNotifications = (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
  api.get('/notifications', { params }).then((r) => r.data);

export const getUnreadNotificationCount = () =>
  api.get('/notifications/unread-count').then((r) => r.data.count as number);

export const markNotificationRead = (id: number) =>
  api.patch(`/notifications/${id}/read`).then((r) => r.data);

export const markAllNotificationsRead = () =>
  api.patch('/notifications/read-all').then((r) => r.data);

export const subscribePush = (subscription: PushSubscriptionJSON) =>
  api.post('/notifications/push/subscribe', subscription).then((r) => r.data);

export const unsubscribePush = (endpoint: string) =>
  api.delete('/notifications/push/unsubscribe', { data: { endpoint } }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Chart of Accounts
// ═══════════════════════════════════════════════════════════════════════════════

export const listAccounts = (params?: { accountType?: string; isActive?: string; search?: string }) =>
  api.get('/accounts', { params }).then((r) => r.data);

export const getAccount = (id: number) =>
  api.get(`/accounts/${id}`).then((r) => r.data);

export const createAccount = (data: Record<string, any>) =>
  api.post('/accounts', data).then((r) => r.data);

export const updateAccount = (id: number, data: Record<string, any>) =>
  api.put(`/accounts/${id}`, data).then((r) => r.data);

export const listAccountGroups = () =>
  api.get('/accounts/groups').then((r) => r.data);

export const createAccountGroup = (data: Record<string, any>) =>
  api.post('/accounts/groups', data).then((r) => r.data);

export const getTrialBalance = (params?: { dateFrom?: string; dateTo?: string }) =>
  api.get('/accounts/trial-balance', { params }).then((r) => r.data);

export const seedAccountsFromOdoo = (accounts: any[]) =>
  api.post('/accounts/seed-odoo', { accounts }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Cost Centers
// ═══════════════════════════════════════════════════════════════════════════════

export const listCostCenters = (params?: { type?: string; parentId?: number; isActive?: string }) =>
  api.get('/cost-centers', { params }).then((r) => r.data);

export const getCostCenter = (id: number) =>
  api.get(`/cost-centers/${id}`).then((r) => r.data);

export const getCostCenterTree = () =>
  api.get('/cost-centers/tree').then((r) => r.data);

export const createCostCenter = (data: Record<string, any>) =>
  api.post('/cost-centers', data).then((r) => r.data);

export const updateCostCenter = (id: number, data: Record<string, any>) =>
  api.put(`/cost-centers/${id}`, data).then((r) => r.data);

export const getCostCenterReport = (id: number, params?: { dateFrom?: string; dateTo?: string }) =>
  api.get(`/cost-centers/${id}/report`, { params }).then((r) => r.data);

export const createDealCostCenter = (data: { dealCode: string; parentCostCenterId: number }) =>
  api.post('/cost-centers/deal', data).then((r) => r.data);

// ── Cost Center Analytics (Phase 20) ────────────────────────────────────────

export const getCostCenterPnL = (id: number, params: { dateFrom: string; dateTo: string }) =>
  api.get(`/cost-center-analytics/pnl/${id}`, { params }).then((r) => r.data);

export const compareCostCenters = (params: { ids: string; dateFrom: string; dateTo: string }) =>
  api.get('/cost-center-analytics/compare', { params }).then((r) => r.data);

export const getCostCenterTrend = (id: number, params?: { year?: number }) =>
  api.get(`/cost-center-analytics/trend/${id}`, { params }).then((r) => r.data);

export const getCostCenterRanking = (params: { dateFrom: string; dateTo: string; metric?: string; limit?: number }) =>
  api.get('/cost-center-analytics/ranking', { params }).then((r) => r.data);

export const getDealProfitability = (params?: { dateFrom?: string; dateTo?: string; dealCode?: string; limit?: number }) =>
  api.get('/cost-center-analytics/deal-profitability', { params }).then((r) => r.data);

export const getCostCenterAllocationSummary = (params: { dateFrom: string; dateTo: string }) =>
  api.get('/cost-center-analytics/allocation-summary', { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Journals
// ═══════════════════════════════════════════════════════════════════════════════

export const listJournals = (params?: { type?: string; isActive?: string; search?: string }) =>
  api.get('/accounting/journals', { params }).then((r) => r.data);

export const getJournal = (id: number) =>
  api.get(`/accounting/journals/${id}`).then((r) => r.data);

export const createJournal = (data: any) =>
  api.post('/accounting/journals', data).then((r) => r.data);

export const updateJournal = (id: number, data: any) =>
  api.put(`/accounting/journals/${id}`, data).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Journal Entries
// ═══════════════════════════════════════════════════════════════════════════════

export const listJournalEntries = (params?: {
  journalId?: number; moveType?: string; state?: string;
  dateFrom?: string; dateTo?: string; sourceModule?: string;
  search?: string; page?: number; pageSize?: number;
}) => api.get('/accounting/entries', { params }).then((r) => r.data);

export const getJournalEntry = (id: number) =>
  api.get(`/accounting/entries/${id}`).then((r) => r.data);

export const getJournalEntryByCode = (code: string) =>
  api.get(`/accounting/entries/code/${code}`).then((r) => r.data);

export const createJournalEntry = (data: Record<string, any>) =>
  api.post('/accounting/entries', data).then((r) => r.data);

export const updateJournalEntry = (id: number, data: Record<string, any>) =>
  api.put(`/accounting/entries/${id}`, data).then((r) => r.data);

export const postJournalEntry = (id: number) =>
  api.post(`/accounting/entries/${id}/post`).then((r) => r.data);

export const cancelJournalEntry = (id: number, reason?: string) =>
  api.post(`/accounting/entries/${id}/cancel`, { reason }).then((r) => r.data);

export const resetJournalEntryToDraft = (id: number) =>
  api.post(`/accounting/entries/${id}/reset-draft`).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Payments
// ═══════════════════════════════════════════════════════════════════════════════

export const listPayments = (params?: {
  paymentType?: string; partnerType?: string; state?: string;
  dateFrom?: string; dateTo?: string; search?: string;
  page?: number; pageSize?: number;
}) => api.get('/accounting/payments', { params }).then((r) => r.data);

export const getPayment = (id: number) =>
  api.get(`/accounting/payments/${id}`).then((r) => r.data);

export const createPayment = (data: Record<string, any>) =>
  api.post('/accounting/payments', data).then((r) => r.data);

export const postPayment = (id: number) =>
  api.post(`/accounting/payments/${id}/post`).then((r) => r.data);

export const cancelAccPayment = (id: number) =>
  api.post(`/accounting/payments/${id}/cancel`).then((r) => r.data);

export const bulkRegisterPayment = (data: {
  invoiceIds: number[];
  journalId: number;
  costCenterId: number;
  date?: string;
  ref?: string;
  amount?: number;
}) => api.post('/accounting/payments/bulk-register', data).then((r) => r.data);

export const getPaymentsByPartner = (
  partnerId: number,
  params?: { paymentType?: string; dateFrom?: string; dateTo?: string },
) => api.get(`/accounting/payments/by-partner/${partnerId}`, { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Reconciliation & Reports
// ═══════════════════════════════════════════════════════════════════════════════

export const reconcileLines = (data: { debitLineId: number; creditLineId: number; amount: number }) =>
  api.post('/accounting/reconcile', data).then((r) => r.data);

export const unreconcileLines = (partialReconcileId: number) =>
  api.delete(`/accounting/reconcile/${partialReconcileId}`).then((r) => r.data);

export const getOpenItems = (params: {
  partnerId?: number;
  accountId?: number;
  accountType?: string;
  side?: 'debit' | 'credit' | 'all';
}) => api.get('/accounting/reconcile/open-items', { params }).then((r) => r.data);

export const autoReconcile = (data: {
  partnerId: number;
  accountId?: number;
  accountType?: string;
}) => api.post('/accounting/reconcile/auto', data).then((r) => r.data);

export const manualReconcile = (data: {
  debitLineIds: number[];
  creditLineIds: number[];
  writeOff?: {
    accountId: number;
    costCenterId: number;
    journalId: number;
    amount: number;
    narration?: string;
  };
}) => api.post('/accounting/reconcile/manual', data).then((r) => r.data);

export const getGeneralLedger = (accountId: number, params?: { dateFrom?: string; dateTo?: string }) =>
  api.get(`/accounting/ledger/${accountId}`, { params }).then((r) => r.data);

export const getPartnerBalance = (partnerId: number) =>
  api.get(`/accounting/partner-balance/${partnerId}`).then((r) => r.data);

export const getJournalSummary = (journalId: number, params?: { dateFrom?: string; dateTo?: string }) =>
  api.get(`/accounting/journal-summary/${journalId}`, { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Financial Reports (Phase 6)
// ═══════════════════════════════════════════════════════════════════════════════

export const getBalanceSheet = (params: {
  asOfDate: string;
  compareAsOfDate?: string;
  costCenterIds?: string;
}) => api.get('/financial-reports/balance-sheet', { params }).then((r) => r.data);

export const getProfitAndLoss = (params: {
  dateFrom: string;
  dateTo: string;
  compareDateFrom?: string;
  compareDateTo?: string;
  costCenterIds?: string;
}) => api.get('/financial-reports/profit-and-loss', { params }).then((r) => r.data);

export const getCashFlowStatement = (params: {
  dateFrom: string;
  dateTo: string;
  costCenterIds?: string;
}) => api.get('/financial-reports/cash-flow', { params }).then((r) => r.data);

export const getReportDrillDown = (params: {
  accountId: number;
  dateFrom?: string;
  dateTo?: string;
  costCenterIds?: string;
}) => api.get('/financial-reports/drill-down', { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Aging Reports & Partner Ledger (Phase 7)
// ═══════════════════════════════════════════════════════════════════════════════

export const getAgedReceivables = (params: {
  asOfDate: string;
  partnerIds?: string;
  costCenterIds?: string;
}) => api.get('/aging-reports/receivables', { params }).then((r) => r.data);

export const getAgedPayables = (params: {
  asOfDate: string;
  partnerIds?: string;
  costCenterIds?: string;
}) => api.get('/aging-reports/payables', { params }).then((r) => r.data);

export const getPartnerLedger = (params: {
  partnerId: number;
  dateFrom?: string;
  dateTo?: string;
  accountTypes?: string;
  openItemsOnly?: boolean;
}) => api.get('/aging-reports/partner-ledger', { params }).then((r) => r.data);

export const getPartnerStatement = (params: {
  partnerId: number;
  asOfDate: string;
}) => api.get('/aging-reports/partner-statement', { params }).then((r) => r.data);

export const listPartnersWithActivity = (params?: {
  partnerType?: 'customer' | 'vendor';
  search?: string;
  limit?: number;
}) => api.get('/aging-reports/partners', { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Dashboard & Analytics (Phase 8)
// ═══════════════════════════════════════════════════════════════════════════════

export const getFinancialOverview = (params?: {
  dateFrom?: string;
  dateTo?: string;
  costCenterIds?: string;
}) => api.get('/accounting-dashboard/overview', { params }).then((r) => r.data);

export const getCashPosition = (params?: {
  asOfDate?: string;
}) => api.get('/accounting-dashboard/cash-position', { params }).then((r) => r.data);

export const getMonthlyTrend = (params?: {
  year?: number;
  months?: number;
  costCenterIds?: string;
}) => api.get('/accounting-dashboard/monthly-trend', { params }).then((r) => r.data);

export const getExpenseByCategory = (params?: {
  dateFrom?: string;
  dateTo?: string;
  costCenterIds?: string;
  limit?: number;
}) => api.get('/accounting-dashboard/expense-by-category', { params }).then((r) => r.data);

export const getExpenseByCostCenter = (params?: {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}) => api.get('/accounting-dashboard/expense-by-cost-center', { params }).then((r) => r.data);

export const getTopCustomers = (params?: {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}) => api.get('/accounting-dashboard/top-customers', { params }).then((r) => r.data);

export const getTopVendors = (params?: {
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
}) => api.get('/accounting-dashboard/top-vendors', { params }).then((r) => r.data);

export const getDSO = (params?: {
  dateFrom?: string;
  dateTo?: string;
}) => api.get('/accounting-dashboard/dso', { params }).then((r) => r.data);

export const getDPO = (params?: {
  dateFrom?: string;
  dateTo?: string;
}) => api.get('/accounting-dashboard/dpo', { params }).then((r) => r.data);

export const getActionItems = () =>
  api.get('/accounting-dashboard/action-items').then((r) => r.data);

export const getJournalActivity = () =>
  api.get('/accounting-dashboard/journal-activity').then((r) => r.data);

export const getRecentEntries = (params?: {
  limit?: number;
}) => api.get('/accounting-dashboard/recent-entries', { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Settings & Configuration (Phase 17)
// ═══════════════════════════════════════════════════════════════════════════════

export const getAccountingSettings = () =>
  api.get('/accounting/settings').then((r) => r.data);

export const saveAccountingSettings = (data: Record<string, any>) =>
  api.post('/accounting/settings', data).then((r) => r.data);

export const getResolvedDefaultAccount = (purpose: string) =>
  api.get(`/accounting/settings/default/${purpose}`).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Bank Reconciliation (Phase 9)
// ═══════════════════════════════════════════════════════════════════════════════

export const listBankStatements = (params?: {
  journalId?: number;
  state?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}) => api.get('/bank-reconciliation/statements', { params }).then((r) => r.data);

export const getBankStatement = (id: number) =>
  api.get(`/bank-reconciliation/statements/${id}`).then((r) => r.data);

export const createBankStatement = (data: {
  journalId: number;
  name?: string;
  date: string;
  balanceStart?: number;
  balanceEnd?: number;
  lines?: Array<{ date: string; paymentRef?: string; amount: number }>;
}) => api.post('/bank-reconciliation/statements', data).then((r) => r.data);

export const importBankStatementCSV = (data: {
  journalId: number;
  name?: string;
  csvContent: string;
  mapping: {
    date: number;
    description: number;
    amount?: number;
    debit?: number;
    credit?: number;
    reference?: number;
    dateFormat?: 'iso' | 'dmy' | 'mdy';
    skipFirstRow?: boolean;
  };
  balanceStart?: number;
  balanceEnd?: number;
}) => api.post('/bank-reconciliation/statements/import', data).then((r) => r.data);

export const deleteBankStatement = (id: number) =>
  api.delete(`/bank-reconciliation/statements/${id}`).then((r) => r.data);

export const closeBankStatement = (id: number) =>
  api.post(`/bank-reconciliation/statements/${id}/close`).then((r) => r.data);

export const reopenBankStatement = (id: number) =>
  api.post(`/bank-reconciliation/statements/${id}/reopen`).then((r) => r.data);

export const autoMatchStatement = (id: number, minConfidence?: 'low' | 'medium' | 'high') =>
  api.post(`/bank-reconciliation/statements/${id}/auto-match`, { minConfidence }).then((r) => r.data);

export const updateBankStatementLine = (
  lineId: number,
  data: { date?: string; paymentRef?: string; amount?: number; partnerId?: number | null },
) => api.put(`/bank-reconciliation/lines/${lineId}`, data).then((r) => r.data);

export const getSuggestedMatches = (lineId: number, limit: number = 10) =>
  api.get(`/bank-reconciliation/lines/${lineId}/suggestions`, { params: { limit } }).then((r) => r.data);

export const matchBankStatementLine = (lineId: number, entryLineId: number) =>
  api.post(`/bank-reconciliation/lines/${lineId}/match`, { entryLineId }).then((r) => r.data);

export const unmatchBankStatementLine = (lineId: number) =>
  api.post(`/bank-reconciliation/lines/${lineId}/unmatch`).then((r) => r.data);

export const createEntryFromStatementLine = (
  lineId: number,
  data: {
    counterAccountId: number;
    costCenterId: number;
    description?: string;
    partnerId?: number;
    partnerName?: string;
  },
) => api.post(`/bank-reconciliation/lines/${lineId}/create-entry`, data).then((r) => r.data);

export const getBankReconciliationSummary = (journalId: number) =>
  api.get('/bank-reconciliation/summary', { params: { journalId } }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Invoices (AR)
// ═══════════════════════════════════════════════════════════════════════════════

export const listInvoices = (params?: {
  moveType?: string; state?: string; paymentState?: string;
  partnerId?: number; dateFrom?: string; dateTo?: string;
  search?: string; page?: number; pageSize?: number;
}) => api.get('/invoices', { params }).then((r) => r.data);

export const getInvoice = (id: number) =>
  api.get(`/invoices/${id}`).then((r) => r.data);

export const createInvoice = (data: Record<string, any>) =>
  api.post('/invoices', data).then((r) => r.data);

export const updateInvoice = (id: number, data: Record<string, any>) =>
  api.put(`/invoices/${id}`, data).then((r) => r.data);

export const postInvoice = (id: number) =>
  api.post(`/invoices/${id}/post`).then((r) => r.data);

export const cancelInvoice = (id: number, reason?: string) =>
  api.post(`/invoices/${id}/cancel`, { reason }).then((r) => r.data);

export const resetInvoiceToDraft = (id: number) =>
  api.post(`/invoices/${id}/reset-to-draft`).then((r) => r.data);

export const registerInvoicePayment = (id: number, data: { amount: number; journalId: number; costCenterId: number; date?: string; ref?: string }) =>
  api.post(`/invoices/${id}/register-payment`, data).then((r) => r.data);

export const createCreditNote = (invoiceId: number, data: { method: 'full_refund' | 'partial_refund' | 'modify'; reason?: string; refundDate?: string; lines?: any[]; autoPost?: boolean }) =>
  api.post(`/invoices/${invoiceId}/credit-note`, data).then((r) => r.data as { creditNote: any; newDraft: any | null });

export const duplicateInvoice = (id: number) =>
  api.post(`/invoices/${id}/duplicate`).then((r) => r.data);

export const getInvoiceStats = () =>
  api.get('/invoices/stats').then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Vendor Bills (AP)
// ═══════════════════════════════════════════════════════════════════════════════

export const listVendorBills = (params?: {
  moveType?: string; state?: string; paymentState?: string;
  partnerId?: number; dateFrom?: string; dateTo?: string;
  search?: string; page?: number; pageSize?: number;
}) => api.get('/vendor-bills', { params }).then((r) => r.data);

export const getVendorBill = (id: number) =>
  api.get(`/vendor-bills/${id}`).then((r) => r.data);

export const createVendorBill = (data: Record<string, any>) =>
  api.post('/vendor-bills', data).then((r) => r.data);

export const createBillFromPO = (poId: number) =>
  api.post(`/vendor-bills/from-po/${poId}`).then((r) => r.data);

export const createBillFromGRN = (grnId: number) =>
  api.post(`/vendor-bills/from-grn/${grnId}`).then((r) => r.data);

export const updateVendorBill = (id: number, data: Record<string, any>) =>
  api.put(`/vendor-bills/${id}`, data).then((r) => r.data);

export const postVendorBill = (
  id: number,
  options?: { overrideVariance?: boolean; varianceOverrideReason?: string },
) => api.post(`/vendor-bills/${id}/post`, options || {}).then((r) => r.data);

export const cancelVendorBill = (id: number, reason?: string) =>
  api.post(`/vendor-bills/${id}/cancel`, { reason }).then((r) => r.data);

export const resetVendorBillToDraft = (id: number) =>
  api.post(`/vendor-bills/${id}/reset-to-draft`).then((r) => r.data);

export const registerBillPayment = (id: number, data: { amount: number; journalId: number; costCenterId: number; date?: string; ref?: string }) =>
  api.post(`/vendor-bills/${id}/register-payment`, data).then((r) => r.data);

export const createDebitNote = (billId: number, data: { method: 'full_refund' | 'partial_refund' | 'modify'; reason?: string; refundDate?: string; lines?: any[]; autoPost?: boolean }) =>
  api.post(`/vendor-bills/${billId}/debit-note`, data).then((r) => r.data as { debitNote: any; newDraft: any | null });

export const createBillFromReturn = (returnId: number) =>
  api.post(`/vendor-bills/from-return/${returnId}`).then((r) => r.data);

export const getReturnsPendingAccounting = () =>
  api.get(`/vendor-bills/pending-from-returns`).then((r) => r.data);

export const duplicateVendorBill = (id: number) =>
  api.post(`/vendor-bills/${id}/duplicate`).then((r) => r.data);

export const threeWayMatch = (billId: number) =>
  api.get(`/vendor-bills/${billId}/match`).then((r) => r.data);

export const getVendorBillStats = () =>
  api.get('/vendor-bills/stats').then((r) => r.data);

export const getBillsForPO = (poCode: string) =>
  api.get(`/vendor-bills/by-po/${poCode}`).then((r) => r.data);

export const getBillsForVendor = (vendorId: number) =>
  api.get(`/vendor-bills/by-vendor/${vendorId}`).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// ACCOUNTING — Taxes & Config
// ═══════════════════════════════════════════════════════════════════════════════

export const listTaxes = (params?: { typeUse?: string; isActive?: string }) =>
  api.get('/taxes', { params }).then((r) => r.data);

export const getTax = (id: number) =>
  api.get(`/taxes/${id}`).then((r) => r.data);

export const createTax = (data: Record<string, any>) =>
  api.post('/taxes', data).then((r) => r.data);

export const calculateTax = (data: { taxId?: number; taxIds?: number[]; baseAmount: number }) =>
  api.post('/taxes/calculate', data).then((r) => r.data);

export const listTaxGroups = () =>
  api.get('/taxes/groups').then((r) => r.data);

export const listPaymentTerms = (params?: { activeOnly?: string }) =>
  api.get('/taxes/payment-terms', { params }).then((r) => r.data);

export const getPaymentTerm = (id: number) =>
  api.get(`/taxes/payment-terms/${id}`).then((r) => r.data);

export const listFiscalPositions = () =>
  api.get('/taxes/fiscal-positions').then((r) => r.data);

export const seedTaxData = (data: Record<string, any>) =>
  api.post('/taxes/seed-odoo', data).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// TAX REPORTS — Accounting Phase 10
// ═══════════════════════════════════════════════════════════════════════════════

export interface VATReturnPeriodParams {
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;
}

export const getVATReturn = (params: VATReturnPeriodParams) =>
  api.get('/tax-reports/vat-return', { params }).then((r) => r.data);

export const getWithholdingTaxReport = (params: VATReturnPeriodParams) =>
  api.get('/tax-reports/withholding', { params }).then((r) => r.data);

export const getStampDutyReport = (params: VATReturnPeriodParams) =>
  api.get('/tax-reports/stamp-duty', { params }).then((r) => r.data);

export const getTaxAuditTrail = (taxId: number, params: VATReturnPeriodParams) =>
  api.get(`/tax-reports/audit-trail/${taxId}`, { params }).then((r) => r.data);

// ═══════════════════════════════════════════════════════════════════════════════
// FX / MULTI-CURRENCY — Accounting Phase 11
// ═══════════════════════════════════════════════════════════════════════════════

export interface FxAnchors {
  gainAccountId: number;
  lossAccountId: number;
  exchangeJournalId: number;
  gainAccountCode: string;
  lossAccountCode: string;
  exchangeJournalCode: string;
  source: 'code_lookup' | 'acc_settings_fk';
}

export interface FxRateHistoryRow {
  rate: number;
  rateDate: string; // YYYY-MM-DD
  source: string | null;
}

export interface FxRateHistoryResponse {
  base: string;
  target: string;
  days: number;
  rates: FxRateHistoryRow[];
}

export interface FxDeviation {
  current: number;
  currentDate: string;
  average: number;
  deviationPct: number;
  warn: boolean;
  windowDays: number;
  sampleSize: number;
}

export interface FxManualRatePayload {
  baseCurrency: string;
  targetCurrency?: string;
  rate: number;
  rateDate?: string; // YYYY-MM-DD
  note?: string;
}

export interface FxRevaluePayload {
  asOfDate: string; // YYYY-MM-DD
  partnerId?: number;
  accountId?: number;
  preview?: boolean;
}

export interface FxRevalueLine {
  lineId: number;
  entryId: number;
  entryCode: string;
  partnerName: string | null;
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  costCenterId: number;
  currencyCode: string;
  amountCurrency: number;
  originalRate: number;
  asOfRate: number;
  originalEgp: number;
  asOfEgp: number;
  delta: number;
  kind: 'gain' | 'loss' | 'none';
  side: 'debit' | 'credit';
}

export interface FxRevalueResult {
  asOfDate: string;
  preview: boolean;
  lines: FxRevalueLine[];
  totalGain: number;
  totalLoss: number;
  journalEntryId: number | null;
}

export const getFxAnchors = (): Promise<FxAnchors> =>
  api.get('/fx/anchors').then((r) => r.data);

export const getFxLatestRates = (): Promise<Array<{ currency: string; rate: number; date: string }>> =>
  api.get('/fx/rates/latest').then((r) => r.data);

export const getFxRateHistory = (base: string, target: string = 'EGP', days: number = 30): Promise<FxRateHistoryResponse> =>
  api.get('/fx/rates/history', { params: { base, target, days } }).then((r) => r.data);

export const getFxRateDeviation = (
  base: string,
  target: string = 'EGP',
  windowDays: number = 7,
  thresholdPct: number = 5,
): Promise<FxDeviation | null> =>
  api.get('/fx/rates/deviation', { params: { base, target, windowDays, thresholdPct } }).then((r) => r.data);

export const setFxManualRate = (data: FxManualRatePayload) =>
  api.post('/fx/rates/manual', data).then((r) => r.data);

export const revalueFxOpenItems = (data: FxRevaluePayload): Promise<FxRevalueResult> =>
  api.post('/fx/revalue', data).then((r) => r.data);

// =====================================================================
// Inventory — Phase 1: Code Dictionary + Part Number Builder
// =====================================================================

export type InventoryDictionaryDimension =
  | 'class'
  | 'item'
  | 'color'
  | 'size'
  | 'supplier'
  | 'softness';

export interface CodeDictionaryEntry {
  id: number;
  dimension: InventoryDictionaryDimension;
  code: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const listCodeDictionary = async (params?: {
  dimension?: InventoryDictionaryDimension;
  active?: boolean;
  search?: string;
}) => {
  const { data } = await api.get('/inventory/code-dictionary', { params });
  return data as { entries: CodeDictionaryEntry[] };
};

export const getCodeDictionaryEntry = async (id: number) => {
  const { data } = await api.get(`/inventory/code-dictionary/${id}`);
  return data as CodeDictionaryEntry;
};

export const createCodeDictionaryEntry = async (payload: {
  dimension: InventoryDictionaryDimension;
  code: string;
  label: string;
  description?: string | null;
  sortOrder?: number;
}) => {
  const { data } = await api.post('/inventory/code-dictionary', payload);
  return data as CodeDictionaryEntry;
};

export const updateCodeDictionaryEntry = async (
  id: number,
  payload: {
    label?: string;
    description?: string | null;
    sortOrder?: number;
    isActive?: boolean;
  }
) => {
  const { data } = await api.patch(`/inventory/code-dictionary/${id}`, payload);
  return data as CodeDictionaryEntry;
};

export const deleteCodeDictionaryEntry = async (id: number) => {
  const { data } = await api.delete(`/inventory/code-dictionary/${id}`);
  return data as CodeDictionaryEntry;
};

export interface PartNumberSegments {
  classCode: string;
  itemCode: string;
  colorCode: string;
  colorVariant: string;
  sizeCode: string;
}

export interface PartNumberValidationResult {
  valid: boolean;
  issues: string[];
  segments: PartNumberSegments | null;
}

export const buildPartNumber = async (segments: PartNumberSegments) => {
  const { data } = await api.post('/inventory/part-number/build', segments);
  return data as { partNumber: string };
};

export const parsePartNumber = async (partNumber: string) => {
  const { data } = await api.post('/inventory/part-number/parse', { partNumber });
  return data as { partNumber: string; segments: PartNumberSegments | null; matched: boolean };
};

export const validatePartNumber = async (partNumber: string) => {
  const { data } = await api.post('/inventory/part-number/validate', { partNumber });
  return data as PartNumberValidationResult;
};

// =====================================================================
// Inventory — Phase 2: Warehouses / Locations / Stock On Hand (read)
// =====================================================================

export interface InventoryWarehouseRef {
  id: number;
  code: string;
  name: string;
}

export interface InventoryWarehouse {
  id: number;
  code: string;
  name: string;
  address: string | null;
  costCenterId: number;
  managerId: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  defaultInputLocationId: number | null;
  defaultOutputLocationId: number | null;
  defaultStockLocationId: number | null;
  defaultScrapLocationId: number | null;
  defaultQcLocationId: number | null;
  defaultProductionLocationId: number | null;
  defaultTransitLocationId: number | null;
  costCenter?: { id: number; code: string; name: string };
  manager?: { id: number; displayName: string; email: string } | null;
  defaultInputLocation?: { id: number; code: string; name: string } | null;
  defaultOutputLocation?: { id: number; code: string; name: string } | null;
  defaultStockLocation?: { id: number; code: string; name: string } | null;
  defaultScrapLocation?: { id: number; code: string; name: string } | null;
  defaultQcLocation?: { id: number; code: string; name: string } | null;
  defaultProductionLocation?: { id: number; code: string; name: string } | null;
  defaultTransitLocation?: { id: number; code: string; name: string } | null;
  _count?: { locations: number };
}

export interface CreateWarehousePayload {
  code: string;
  name: string;
  address?: string | null;
  costCenterId: number;
  managerId?: number | null;
}

export interface UpdateWarehousePayload {
  name?: string;
  address?: string | null;
  costCenterId?: number;
  managerId?: number | null;
  isActive?: boolean;
  defaultInputLocationId?: number | null;
  defaultOutputLocationId?: number | null;
  defaultStockLocationId?: number | null;
  defaultScrapLocationId?: number | null;
  defaultQcLocationId?: number | null;
  defaultProductionLocationId?: number | null;
  defaultTransitLocationId?: number | null;
}

export const listWarehouses = async (params?: {
  active?: boolean;
  costCenterId?: number;
  search?: string;
}) => {
  const { data } = await api.get('/inventory/warehouses', { params });
  return data as { warehouses: InventoryWarehouse[] };
};

export const getWarehouse = async (id: number) => {
  const { data } = await api.get(`/inventory/warehouses/${id}`);
  return data as InventoryWarehouse;
};

export const createWarehouse = async (payload: CreateWarehousePayload) => {
  const { data } = await api.post('/inventory/warehouses', payload);
  return data as InventoryWarehouse;
};

export const updateWarehouse = async (id: number, payload: UpdateWarehousePayload) => {
  const { data } = await api.patch(`/inventory/warehouses/${id}`, payload);
  return data as InventoryWarehouse;
};

export const deactivateWarehouse = async (id: number) => {
  const { data } = await api.delete(`/inventory/warehouses/${id}`);
  return data as InventoryWarehouse;
};

export type InventoryLocationType =
  | 'internal'
  | 'input'
  | 'output'
  | 'scrap'
  | 'qc_hold'
  | 'production'
  | 'transit'
  | 'consignment_in'
  | 'consignment_out'
  | 'dropship';

export type InventoryRemovalStrategy =
  | 'fifo'
  | 'lifo'
  | 'fefo'
  | 'closest_expiry'
  | 'least_quantity';

export interface InventoryLocation {
  id: number;
  warehouseId: number;
  code: string;
  name: string;
  locationType: InventoryLocationType;
  parentLocationId: number | null;
  barcode: string | null;
  isActive: boolean;
  maxWeightKg: string | null;
  maxVolumeM3: string | null;
  removalStrategy: InventoryRemovalStrategy | null;
  createdAt: string;
  updatedAt: string;
  warehouse?: { id: number; code: string; name: string };
  parent?: { id: number; code: string; name: string } | null;
  _count?: { children: number; stockQuants: number };
}

export interface InventoryLocationTreeNode {
  id: number;
  code: string;
  name: string;
  locationType: InventoryLocationType;
  isActive: boolean;
  parentLocationId: number | null;
  barcode: string | null;
  maxWeightKg: string | null;
  maxVolumeM3: string | null;
  removalStrategy: InventoryRemovalStrategy | null;
  children: InventoryLocationTreeNode[];
}

export interface CreateLocationPayload {
  warehouseId: number;
  code: string;
  name: string;
  locationType: InventoryLocationType;
  parentLocationId?: number | null;
  barcode?: string | null;
  maxWeightKg?: string | number | null;
  maxVolumeM3?: string | number | null;
  removalStrategy?: InventoryRemovalStrategy | null;
}

export interface UpdateLocationPayload {
  name?: string;
  locationType?: InventoryLocationType;
  parentLocationId?: number | null;
  barcode?: string | null;
  maxWeightKg?: string | number | null;
  maxVolumeM3?: string | number | null;
  removalStrategy?: InventoryRemovalStrategy | null;
  isActive?: boolean;
}

export const listLocations = async (params?: {
  warehouseId?: number;
  locationType?: string;
  active?: boolean;
  parentLocationId?: number | 'null';
  search?: string;
}) => {
  const { data } = await api.get('/inventory/locations', { params });
  return data as { locations: InventoryLocation[] };
};

export const getLocation = async (id: number) => {
  const { data } = await api.get(`/inventory/locations/${id}`);
  return data as InventoryLocation & {
    children: Array<{
      id: number;
      code: string;
      name: string;
      locationType: InventoryLocationType;
      isActive: boolean;
    }>;
  };
};

export const getLocationTypes = async () => {
  const { data } = await api.get('/inventory/locations/types');
  return data as {
    locationTypes: InventoryLocationType[];
    removalStrategies: InventoryRemovalStrategy[];
  };
};

export const getLocationTree = async (warehouseId: number) => {
  const { data } = await api.get(`/inventory/locations/tree/${warehouseId}`);
  return data as { tree: InventoryLocationTreeNode[] };
};

export const createLocation = async (payload: CreateLocationPayload) => {
  const { data } = await api.post('/inventory/locations', payload);
  return data as InventoryLocation;
};

export const updateLocation = async (id: number, payload: UpdateLocationPayload) => {
  const { data } = await api.patch(`/inventory/locations/${id}`, payload);
  return data as InventoryLocation;
};

export const deactivateLocation = async (id: number) => {
  const { data } = await api.delete(`/inventory/locations/${id}`);
  return data as InventoryLocation;
};

export interface StockOnHandRow {
  id: number;
  catalogItemId: number;
  partNumber: string;
  description: string;
  abcClass: string | null;
  categoryId: number | null;
  lotTracked: boolean;
  warehouseId: number;
  warehouseCode: string;
  warehouseName: string;
  locationId: number;
  locationCode: string;
  locationName: string;
  lotReference: string | null;
  quantity: string;
  reservedQuantity: string;
  availableQuantity: string;
  updatedAt: string;
}

export const listStockOnHand = async (params?: {
  warehouseId?: number;
  locationId?: number;
  catalogItemId?: number;
  categoryId?: number;
  abcClass?: string;
  lotReference?: string;
  includeZero?: boolean;
  search?: string;
}) => {
  const { data } = await api.get('/inventory/stock-on-hand', { params });
  return data as { rows: StockOnHandRow[]; total: number };
};

export interface StockRebuildDiff {
  catalogItemId: number;
  partNumber: string;
  locationId: number;
  locationCode: string;
  lotReference: string | null;
  currentQuantity: string;
  expectedQuantity: string;
  delta: string;
}

export interface StockRebuildResult {
  dryRun: true;
  warehouseId: number | null;
  movesConsidered: number;
  diffs: StockRebuildDiff[];
  missingFromQuants: StockRebuildDiff[];
  phantomQuants: StockRebuildDiff[];
}

export const previewStockRebuild = async (warehouseId?: number) => {
  const { data } = await api.get('/inventory/stock-on-hand/rebuild-preview', {
    params: warehouseId !== undefined ? { warehouseId } : undefined,
  });
  return data as StockRebuildResult;
};

// ────────────────────────────────────────────────────────────────────────────
// Inventory Phase 3 — stock moves, pickings, lots, operation wrappers
// ────────────────────────────────────────────────────────────────────────────

export type InventoryMoveType =
  | 'receipt'
  | 'issue'
  | 'transfer'
  | 'adjustment'
  | 'scrap'
  | 'return'
  | 'sample'
  | 'consume_wip'
  | 'produce_from_wip';

export type WipStage = 'cutting' | 'embroidery' | 'sewing' | 'fitting' | 'finishing';

export type InventoryMoveState =
  | 'draft'
  | 'confirmed'
  | 'assigned'
  | 'done'
  | 'cancelled';

export interface InventoryStockMove {
  id: number;
  moveCode: string;
  catalogItemId: number;
  quantity: string;
  uomId: number;
  sourceLocationId: number;
  destLocationId: number;
  moveType: InventoryMoveType;
  state: InventoryMoveState;
  unitCost: string | null;
  totalCost: string | null;
  lotReference: string | null;
  lotId: number | null;
  referenceDoc: string | null;
  sourceModule: string | null;
  sourceId: string | null;
  costCenterId: number;
  valuationLayerId: number | null;
  journalEntryId: number | null;
  createdAt: string;
  doneDate: string | null;
  notes: string | null;
  catalogItem?: { id: number; partNumber: string; description: string };
  uom?: { id: number; name: string };
  sourceLocation?: {
    id: number;
    code: string;
    name: string;
    warehouse: { id: number; code: string; name: string };
  };
  destLocation?: {
    id: number;
    code: string;
    name: string;
    warehouse: { id: number; code: string; name: string };
  };
  costCenter?: { id: number; code: string; name: string };
  createdBy?: { id: number; displayName: string };
  valuationLayer?: {
    id: number;
    quantity: string;
    unitCost: string;
    value: string;
    remainingQuantity: string;
    remainingValue: string;
    accountingDate: string;
  } | null;
  journalEntry?: {
    id: number;
    entryCode: string;
    state: string;
    amountTotal: string;
    date: string;
  } | null;
  lot?: {
    id: number;
    lotCode: string;
    remainingQuantity: string;
    isActive: boolean;
  } | null;
}

export interface StockMoveListFilter {
  catalogItemId?: number;
  warehouseId?: number;
  sourceLocationId?: number;
  destLocationId?: number;
  moveType?: InventoryMoveType;
  state?: InventoryMoveState;
  sourceModule?: string;
  sourceId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface StockMoveListResult {
  rows: InventoryStockMove[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateStockMovePayload {
  catalogItemId: number;
  quantity: string | number;
  uomId: number;
  sourceLocationId: number;
  destLocationId: number;
  moveType: InventoryMoveType;
  sourceModule?: string | null;
  sourceId?: string | null;
  referenceDoc?: string | null;
  lotId?: number | null;
  lotReference?: string | null;
  unitCost?: string | number | null;
  costCenterId?: number | null;
  notes?: string | null;
  // Phase 12 — WIP fields
  wipStage?: WipStage | null;
  sourceDealId?: number | null;
  sourceJobOrderId?: number | null;
}

export const listStockMoves = async (filter: StockMoveListFilter = {}) => {
  const { data } = await api.get('/inventory/stock-moves', { params: filter });
  return data as StockMoveListResult;
};

export const getStockMove = async (id: number) => {
  const { data } = await api.get(`/inventory/stock-moves/${id}`);
  return data as InventoryStockMove;
};

export const createStockMove = async (payload: CreateStockMovePayload) => {
  const { data } = await api.post('/inventory/stock-moves', payload);
  return data as InventoryStockMove;
};

export const confirmStockMove = async (id: number) => {
  const { data } = await api.post(`/inventory/stock-moves/${id}/confirm`);
  return data as InventoryStockMove;
};

export const assignStockMove = async (id: number) => {
  const { data } = await api.post(`/inventory/stock-moves/${id}/assign`);
  return data as InventoryStockMove;
};

export const validateStockMove = async (id: number) => {
  const { data } = await api.post(`/inventory/stock-moves/${id}/validate`);
  return data as InventoryStockMove;
};

export const cancelStockMove = async (id: number) => {
  const { data } = await api.post(`/inventory/stock-moves/${id}/cancel`);
  return data as InventoryStockMove;
};

export const getStockMoveMeta = async () => {
  const { data } = await api.get('/inventory/stock-moves/meta/types');
  return data as {
    moveTypes: readonly InventoryMoveType[];
    states: readonly InventoryMoveState[];
  };
};

// ── Operation-specific wrappers ───────────────────────────────────────────────

export type CreateOperationMovePayload = Omit<CreateStockMovePayload, 'moveType'>;

export const createReceipt = async (payload: CreateOperationMovePayload) => {
  const { data } = await api.post('/inventory/receipts', payload);
  return data as InventoryStockMove;
};

export const createIssue = async (payload: CreateOperationMovePayload) => {
  const { data } = await api.post('/inventory/issues', payload);
  return data as InventoryStockMove;
};

export const createTransfer = async (payload: CreateOperationMovePayload) => {
  const { data } = await api.post('/inventory/transfers', payload);
  return data as InventoryStockMove;
};

export const createScrap = async (payload: CreateOperationMovePayload) => {
  const { data } = await api.post('/inventory/scrap', payload);
  return data as InventoryStockMove;
};

export const createAdjustment = async (payload: CreateOperationMovePayload) => {
  const { data } = await api.post('/inventory/adjustments', payload);
  return data as InventoryStockMove;
};

// ── WIP (Phase 12) ───────────────────────────────────────────────────────────

export interface CreateProductionIssuePayload extends CreateOperationMovePayload {
  wipStage: WipStage;
  sourceDealId?: number | null;
  sourceJobOrderId?: number | null;
}

export const createProductionIssue = async (payload: CreateProductionIssuePayload) => {
  const { data } = await api.post('/inventory/wip/consume', payload);
  return data as InventoryStockMove;
};

export const createWipProduction = async (payload: CreateProductionIssuePayload) => {
  const { data } = await api.post('/inventory/wip/produce', payload);
  return data as InventoryStockMove;
};

export interface WipBalanceStage {
  wipStage: string;
  totalValue: number;
  moveCount: number;
}

export interface WipAgingBucket {
  wipStage: string;
  bucket: string;
  totalValue: number;
  moveCount: number;
}

export interface WipStaleAlert {
  moveId: number;
  moveCode: string;
  catalogItemId: number;
  partNumber: string;
  description: string;
  wipStage: string;
  quantity: number;
  totalCost: number;
  doneDate: string;
  ageDays: number;
}

export const getWipBalance = async () => {
  const { data } = await api.get('/inventory/wip/balance');
  return data as { stages: WipBalanceStage[]; grandTotal: number };
};

export const getWipAging = async () => {
  const { data } = await api.get('/inventory/wip/aging');
  return data as { buckets: WipAgingBucket[] };
};

export const getWipStaleAlerts = async (thresholdDays?: number) => {
  const { data } = await api.get('/inventory/wip/stale-alerts', {
    params: thresholdDays !== undefined ? { thresholdDays } : undefined,
  });
  return data as { alerts: WipStaleAlert[]; thresholdDays: number };
};

// ── Barcodes (Phase 13) ─────────────────────────────────────────────────────

export type BarcodeEntityType = 'item' | 'lot' | 'location';
export type BarcodeLabelTemplate = 'avery_30up' | 'adhesive_roll' | 'garment_hangtag';

export interface BarcodeLookupResult {
  entityType: BarcodeEntityType;
  id: number;
  code: string;
  name: string;
  extra?: Record<string, unknown>;
}

export const lookupBarcode = async (data: string) => {
  const { data: result } = await api.post('/inventory/barcodes/lookup', { data });
  return result as BarcodeLookupResult;
};

export const generateBarcodeLabels = async (
  template: BarcodeLabelTemplate,
  entityType: BarcodeEntityType,
  ids: number[],
  copies?: number,
): Promise<Blob> => {
  const { data } = await api.post('/inventory/barcodes/labels', {
    template, entityType, ids, copies,
  }, { responseType: 'blob' });
  return data as Blob;
};

export const getItemBarcodeUrl = (id: number, format: 'qr' | 'code128' = 'qr') =>
  `/api/inventory/barcodes/item/${id}${format === 'code128' ? '/code128' : ''}`;

export const getLotBarcodeUrl = (id: number, format: 'qr' | 'code128' = 'qr') =>
  `/api/inventory/barcodes/lot/${id}${format === 'code128' ? '/code128' : ''}`;

export const getLocationBarcodeUrl = (id: number, format: 'qr' | 'code128' = 'qr') =>
  `/api/inventory/barcodes/location/${id}${format === 'code128' ? '/code128' : ''}`;

// ── Inventory Reports (Phase 14) ──────���──────────────────────────────────────

export interface StockAgingRow {
  catalogItemId: number;
  itemCode: string;
  itemName: string;
  abcClass: string | null;
  buckets: { label: string; quantity: string; value: string }[];
  totalQuantity: string;
  totalValue: string;
}

export interface DeadStockRow {
  catalogItemId: number;
  itemCode: string;
  itemName: string;
  abcClass: string | null;
  onHandQuantity: string;
  onHandValue: string;
  daysSinceLastIssue: number | null;
  lastIssueDate: string | null;
}

export interface SlowMovingRow {
  catalogItemId: number;
  itemCode: string;
  itemName: string;
  abcClass: string | null;
  onHandQuantity: string;
  issuedQuantity: string;
  velocityPerDay: string;
  daysOfStock: string;
}

export interface TurnoverRow {
  catalogItemId: number;
  itemCode: string;
  itemName: string;
  categoryName: string | null;
  abcClass: string | null;
  avgInventoryValue: string;
  cogsValue: string;
  turnoverRatio: string;
  daysToTurn: string;
}

export interface ReconciliationCheck {
  name: string;
  status: 'balanced' | 'variance' | 'error';
  details: Record<string, string | number>;
  variance: string;
  checkedAt: string;
}

export interface SnapshotSummary {
  snapshotAt: string;
  rowCount: number;
  totalValue: string;
}

export interface StockAtRow {
  catalogItemId: number;
  itemCode: string;
  itemName: string;
  locationId: number;
  locationCode: string;
  lotId: number | null;
  lotCode: string | null;
  quantity: string;
  unitCost: string;
  totalValue: string;
}

export const getStockAging = async () => {
  const { data } = await api.get('/inventory/reports/aging');
  return data as { rows: StockAgingRow[]; total: number };
};

export const getDeadStock = async (thresholdDays: number = 90) => {
  const { data } = await api.get('/inventory/reports/dead-stock', { params: { thresholdDays } });
  return data as { rows: DeadStockRow[]; total: number; thresholdDays: number };
};

export const getSlowMoving = async (periodDays: number = 90) => {
  const { data } = await api.get('/inventory/reports/slow-moving', { params: { periodDays } });
  return data as { rows: SlowMovingRow[]; total: number; periodDays: number };
};

export const getStockTurnover = async (periodDays: number = 90) => {
  const { data } = await api.get('/inventory/reports/turnover', { params: { periodDays } });
  return data as { rows: TurnoverRow[]; total: number; periodDays: number };
};

export const getReconciliation = async () => {
  const { data } = await api.get('/inventory/reports/reconciliation');
  return data as { checks: ReconciliationCheck[]; overallStatus: string; checkedAt: string };
};

export const generateSnapshot = async () => {
  const { data } = await api.post('/inventory/reports/snapshots/generate');
  return data as { rowsCreated: number; snapshotAt: string };
};

export const listSnapshots = async (limit: number = 60) => {
  const { data } = await api.get('/inventory/reports/snapshots', { params: { limit } });
  return data as { rows: SnapshotSummary[] };
};

export const getSnapshotDetail = async (date: string) => {
  const { data } = await api.get(`/inventory/reports/snapshots/${date}`);
  return data as { rows: any[]; total: number; date: string };
};

export const compressSnapshots = async (olderThanMonths: number = 12) => {
  const { data } = await api.post('/inventory/reports/snapshots/compress', { olderThanMonths });
  return data as { deletedRows: number };
};

export const getStockAt = async (date: string) => {
  const { data } = await api.get('/inventory/reports/stock-at', { params: { date } });
  return data as { rows: StockAtRow[]; total: number; asOfDate: string };
};

// ��─ Pickings ──────────────────────────────────────────────────────────────────

// ── Inventory Phase 15 — Dashboard, Webhooks, Item On-Hand ─────────────────

export interface DashboardKPI {
  key: string;
  label: string;
  value: number | string;
  unit?: string;
  link?: string;
}

export interface InventoryDashboardData {
  kpis: DashboardKPI[];
  topMovers: Array<{ catalogItemId: number; partNumber: string; description: string; issuedQty: number }>;
  bottomMovers: Array<{ catalogItemId: number; partNumber: string; description: string; issuedQty: number }>;
}

export const getInventoryDashboard = async (): Promise<InventoryDashboardData> => {
  const { data } = await api.get('/inventory/dashboard');
  return data;
};

export interface ItemOnHandSummary {
  totalOnHand: number;
  totalReserved: number;
  totalAvailable: number;
  belowReorder: boolean;
  reorderPoint: number | null;
  byWarehouse: Array<{ warehouseId: number; warehouseCode: string; warehouseName: string; onHand: number }>;
}

export const getItemOnHand = async (catalogItemId: number): Promise<ItemOnHandSummary> => {
  const { data } = await api.get(`/inventory/dashboard/item-on-hand/${catalogItemId}`);
  return data;
};

export const listWebhooks = async () => {
  const { data } = await api.get('/webhooks');
  return data;
};

export const getWebhookEvents = async () => {
  const { data } = await api.get('/webhooks/events');
  return data;
};

export const createWebhook = async (body: { name: string; targetUrl: string; eventTypes: string[]; secret: string }) => {
  const { data } = await api.post('/webhooks', body);
  return data;
};

export const updateWebhook = async (id: number, body: any) => {
  const { data } = await api.patch(`/webhooks/${id}`, body);
  return data;
};

export const deleteWebhook = async (id: number) => {
  const { data } = await api.delete(`/webhooks/${id}`);
  return data;
};

export const testWebhook = async (id: number) => {
  const { data } = await api.post(`/webhooks/${id}/test`);
  return data;
};

export const getWebhookDeliveries = async (params: { event?: string; status?: string; page?: number; pageSize?: number }) => {
  const { data } = await api.get('/webhooks/deliveries', { params });
  return data as { data: any[]; total: number };
};

export type InventoryPickingType = 'incoming' | 'outgoing' | 'internal';
export type InventoryPickingState =
  | 'draft'
  | 'waiting'
  | 'ready'
  | 'done'
  | 'cancelled';

export interface InventoryPicking {
  id: number;
  pickingCode: string;
  pickingType: InventoryPickingType;
  warehouseId: number;
  sourceLocationId: number | null;
  destLocationId: number | null;
  state: InventoryPickingState;
  scheduledDate: string;
  doneDate: string | null;
  sourceModule: string | null;
  sourceId: string | null;
  partnerId: number | null;
  partnerType: string | null;
  originDoc: string | null;
  notes: string | null;
  createdAt: string;
  warehouse?: { id: number; code: string; name: string; costCenterId?: number };
  sourceLocation?: { id: number; code: string; name: string } | null;
  destLocation?: { id: number; code: string; name: string } | null;
  createdBy?: { id: number; displayName: string };
  moveCount?: number;
  moves?: InventoryStockMove[];
}

export interface PickingListFilter {
  warehouseId?: number;
  state?: InventoryPickingState;
  pickingType?: InventoryPickingType;
  sourceModule?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PickingListResult {
  rows: InventoryPicking[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreatePickingPayload {
  pickingType: InventoryPickingType;
  warehouseId: number;
  sourceLocationId?: number | null;
  destLocationId?: number | null;
  scheduledDate?: string;
  sourceModule?: string | null;
  sourceId?: string | null;
  partnerId?: number | null;
  partnerType?: string | null;
  originDoc?: string | null;
  notes?: string | null;
}

export interface AddPickingMovePayload {
  catalogItemId: number;
  quantity: string | number;
  uomId: number;
  sourceLocationId: number;
  destLocationId: number;
  lotId?: number | null;
  lotReference?: string | null;
  notes?: string | null;
}

export const listPickings = async (filter: PickingListFilter = {}) => {
  const { data } = await api.get('/inventory/pickings', { params: filter });
  return data as PickingListResult;
};

export const getPicking = async (id: number) => {
  const { data } = await api.get(`/inventory/pickings/${id}`);
  return data as InventoryPicking;
};

export const createPicking = async (payload: CreatePickingPayload) => {
  const { data } = await api.post('/inventory/pickings', payload);
  return data as InventoryPicking;
};

export const addMoveToPicking = async (
  pickingId: number,
  payload: AddPickingMovePayload
) => {
  const { data } = await api.post(`/inventory/pickings/${pickingId}/moves`, payload);
  return data as InventoryStockMove;
};

export const confirmPicking = async (id: number) => {
  const { data } = await api.post(`/inventory/pickings/${id}/confirm`);
  return data as InventoryPicking;
};

export const markPickingReady = async (id: number) => {
  const { data } = await api.post(`/inventory/pickings/${id}/ready`);
  return data as InventoryPicking;
};

export const validatePicking = async (id: number) => {
  const { data } = await api.post(`/inventory/pickings/${id}/validate`);
  return data as InventoryPicking;
};

export const cancelPicking = async (id: number) => {
  const { data } = await api.post(`/inventory/pickings/${id}/cancel`);
  return data as InventoryPicking;
};

// ── Lots ──────────────────────────────────────────────────────────────────────

export interface InventoryLot {
  id: number;
  lotCode: string;
  catalogItemId: number;
  initialQuantity: string;
  remainingQuantity: string;
  uomId: number;
  unitCost: string;
  sourceMoveId: number | null;
  sourceGrnLineId: number | null;
  supplierLotRef: string | null;
  receivedAt: string;
  expiresAt: string | null;
  isActive: boolean;
  notes: string | null;
  createdAt: string;
  catalogItem?: { id: number; partNumber: string; description: string; lotTracked: boolean };
  uom?: { id: number; name: string };
  sourceMove?: { id: number; moveCode: string; moveType: InventoryMoveType } | null;
  stockMovesReferencingLot?: Array<{
    id: number;
    moveCode: string;
    moveType: InventoryMoveType;
    state: InventoryMoveState;
    quantity: string;
    doneDate: string | null;
    sourceLocation: { id: number; code: string; name: string };
    destLocation: { id: number; code: string; name: string };
  }>;
}

export interface LotListFilter {
  catalogItemId?: number;
  isActive?: boolean;
  search?: string;
  expiringWithinDays?: number;
}

export const listLots = async (filter: LotListFilter = {}) => {
  const { data } = await api.get('/inventory/lots', { params: filter });
  return data as { lots: InventoryLot[]; total: number };
};

export const getLot = async (id: number) => {
  const { data } = await api.get(`/inventory/lots/${id}`);
  return data as InventoryLot;
};

export const createLot = async (payload: {
  catalogItemId: number;
  initialQuantity: string | number;
  uomId: number;
  unitCost: string | number;
  supplierLotRef?: string | null;
  receivedAt?: string;
  expiresAt?: string | null;
  notes?: string | null;
}) => {
  const { data } = await api.post('/inventory/lots', payload);
  return data as InventoryLot;
};

// ─── Inventory Phase 4 — serials, quality, putaway ─────────────────────────

export type SerialMaintenanceState =
  | 'operational'
  | 'maintenance'
  | 'broken'
  | 'retired';

export interface InventorySerialNumber {
  id: number;
  serialNumber: string;
  catalogItemId: number;
  currentLocationId: number;
  purchaseDate: string | null;
  purchasePrice: string | null;
  warrantyExpiresAt: string | null;
  assignedToUserId: number | null;
  maintenanceState: SerialMaintenanceState;
  lastCalibrationAt: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  catalogItem?: { id: number; partNumber: string; description: string | null; lotTracked?: boolean };
  currentLocation?: {
    id: number;
    code: string;
    name: string;
    warehouse?: InventoryWarehouseRef | null;
  };
  assignedTo?: { id: number; displayName: string; email: string } | null;
}

export interface SerialNumberListParams {
  catalogItemId?: number;
  currentLocationId?: number;
  maintenanceState?: SerialMaintenanceState;
  assignedToUserId?: number;
  isActive?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const listSerialNumbers = async (params?: SerialNumberListParams) => {
  const { data } = await api.get('/inventory/serial-numbers', { params });
  return data as {
    rows: InventorySerialNumber[];
    total: number;
    page: number;
    pageSize: number;
  };
};

export const getSerialNumber = async (id: number) => {
  const { data } = await api.get(`/inventory/serial-numbers/${id}`);
  return data as InventorySerialNumber;
};

export interface CreateSerialNumberPayload {
  serialNumber: string;
  catalogItemId: number;
  currentLocationId: number;
  purchaseDate?: string | null;
  purchasePrice?: string | number | null;
  warrantyExpiresAt?: string | null;
  assignedToUserId?: number | null;
  lastCalibrationAt?: string | null;
  notes?: string | null;
}

export const createSerialNumber = async (payload: CreateSerialNumberPayload) => {
  const { data } = await api.post('/inventory/serial-numbers', payload);
  return data as InventorySerialNumber;
};

export interface UpdateSerialNumberPayload {
  currentLocationId?: number;
  purchaseDate?: string | null;
  purchasePrice?: string | number | null;
  warrantyExpiresAt?: string | null;
  assignedToUserId?: number | null;
  lastCalibrationAt?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export const updateSerialNumber = async (
  id: number,
  payload: UpdateSerialNumberPayload
) => {
  const { data } = await api.patch(`/inventory/serial-numbers/${id}`, payload);
  return data as InventorySerialNumber;
};

export const assignSerialToUser = async (id: number, userId: number) => {
  const { data } = await api.post(`/inventory/serial-numbers/${id}/assign`, { userId });
  return data as InventorySerialNumber;
};

export const unassignSerial = async (id: number) => {
  const { data } = await api.post(`/inventory/serial-numbers/${id}/unassign`);
  return data as InventorySerialNumber;
};

export const setSerialMaintenanceState = async (
  id: number,
  state: SerialMaintenanceState
) => {
  const { data } = await api.post(
    `/inventory/serial-numbers/${id}/maintenance-state`,
    { state }
  );
  return data as InventorySerialNumber;
};

export const deactivateSerialNumber = async (id: number) => {
  const { data } = await api.delete(`/inventory/serial-numbers/${id}`);
  return data as InventorySerialNumber;
};

// ─── Quality inspection ────────────────────────────────────────────────────

export type InspectionState = 'pending' | 'pass' | 'fail' | 'partial';

export interface InspectionTestDef {
  name: string;
  type: 'numeric' | 'boolean' | 'text' | string;
  min?: number;
  max?: number;
  unit?: string;
  [key: string]: unknown;
}

export interface InventoryInspectionPlan {
  id: number;
  name: string;
  catalogItemId: number | null;
  categoryId: number | null;
  tests: InspectionTestDef[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  catalogItem?: { id: number; partNumber: string; description: string | null } | null;
  category?: { id: number; name: string } | null;
  _count?: { inspections: number };
}

export interface InventoryInspection {
  id: number;
  stockMoveId: number;
  planId: number;
  inspectorId: number;
  state: InspectionState;
  results: Record<string, unknown> | null;
  notes: string | null;
  inspectedAt: string | null;
  createdAt: string;
  updatedAt: string;
  stockMove?: {
    id: number;
    moveCode: string;
    moveType: string;
    state: string;
    quantity?: string;
    catalogItem?: { id: number; partNumber: string; description?: string | null };
  };
  plan?: InventoryInspectionPlan;
  inspector?: { id: number; displayName: string; email: string };
}

export const listInspectionPlans = async (params?: {
  catalogItemId?: number;
  categoryId?: number;
  isActive?: boolean;
  search?: string;
}) => {
  const { data } = await api.get('/inventory/quality/plans', { params });
  return data as InventoryInspectionPlan[];
};

export const getInspectionPlan = async (id: number) => {
  const { data } = await api.get(`/inventory/quality/plans/${id}`);
  return data as InventoryInspectionPlan;
};

export interface CreateInspectionPlanPayload {
  name: string;
  catalogItemId?: number | null;
  categoryId?: number | null;
  tests?: InspectionTestDef[];
}

export const createInspectionPlan = async (payload: CreateInspectionPlanPayload) => {
  const { data } = await api.post('/inventory/quality/plans', payload);
  return data as InventoryInspectionPlan;
};

export interface UpdateInspectionPlanPayload {
  name?: string;
  catalogItemId?: number | null;
  categoryId?: number | null;
  tests?: InspectionTestDef[];
  isActive?: boolean;
}

export const updateInspectionPlan = async (
  id: number,
  payload: UpdateInspectionPlanPayload
) => {
  const { data } = await api.patch(`/inventory/quality/plans/${id}`, payload);
  return data as InventoryInspectionPlan;
};

export const deactivateInspectionPlan = async (id: number) => {
  const { data } = await api.post(`/inventory/quality/plans/${id}/deactivate`);
  return data as InventoryInspectionPlan;
};

export const listInspections = async (params?: {
  stockMoveId?: number;
  planId?: number;
  inspectorId?: number;
  state?: InspectionState;
  page?: number;
  pageSize?: number;
}) => {
  const { data } = await api.get('/inventory/quality/inspections', { params });
  return data as {
    rows: InventoryInspection[];
    total: number;
    page: number;
    pageSize: number;
  };
};

export const getInspection = async (id: number) => {
  const { data } = await api.get(`/inventory/quality/inspections/${id}`);
  return data as InventoryInspection;
};

export const createInspection = async (payload: {
  stockMoveId: number;
  planId: number;
  inspectorId: number;
}) => {
  const { data } = await api.post('/inventory/quality/inspections', payload);
  return data as InventoryInspection;
};

export const recordInspection = async (
  id: number,
  payload: {
    results: Record<string, unknown>;
    state: InspectionState;
    notes?: string | null;
  }
) => {
  const { data } = await api.post(
    `/inventory/quality/inspections/${id}/record`,
    payload
  );
  return data as InventoryInspection;
};

// ─── Putaway rules ─────────────────────────────────────────────────────────

export type PutawayMatchReason = 'item' | 'category' | 'warehouse_catchall';

export interface InventoryPutawayRule {
  id: number;
  warehouseId: number;
  catalogItemId: number | null;
  categoryId: number | null;
  lotTracked: boolean | null;
  destinationLocationId: number;
  priority: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  warehouse?: InventoryWarehouseRef | null;
  catalogItem?: { id: number; partNumber: string; description: string | null } | null;
  category?: { id: number; name: string } | null;
  destinationLocation?: {
    id: number;
    code: string;
    name: string;
    warehouseId: number;
  } | null;
}

export const listPutawayRules = async (params?: {
  warehouseId?: number;
  catalogItemId?: number;
  categoryId?: number;
  isActive?: boolean;
}) => {
  const { data } = await api.get('/inventory/putaway-rules', { params });
  return data as InventoryPutawayRule[];
};

export const getPutawayRule = async (id: number) => {
  const { data } = await api.get(`/inventory/putaway-rules/${id}`);
  return data as InventoryPutawayRule;
};

export interface CreatePutawayRulePayload {
  warehouseId: number;
  catalogItemId?: number | null;
  categoryId?: number | null;
  lotTracked?: boolean | null;
  destinationLocationId: number;
  priority?: number;
}

export const createPutawayRule = async (payload: CreatePutawayRulePayload) => {
  const { data } = await api.post('/inventory/putaway-rules', payload);
  return data as InventoryPutawayRule;
};

export interface UpdatePutawayRulePayload {
  catalogItemId?: number | null;
  categoryId?: number | null;
  lotTracked?: boolean | null;
  destinationLocationId?: number;
  priority?: number;
  isActive?: boolean;
}

export const updatePutawayRule = async (
  id: number,
  payload: UpdatePutawayRulePayload
) => {
  const { data } = await api.patch(`/inventory/putaway-rules/${id}`, payload);
  return data as InventoryPutawayRule;
};

export const deactivatePutawayRule = async (id: number) => {
  const { data } = await api.delete(`/inventory/putaway-rules/${id}`);
  return data as InventoryPutawayRule;
};

export interface PutawayResolveResult {
  destinationLocationId: number;
  ruleId: number;
  matchedOn: PutawayMatchReason;
}

export const resolvePutawayRule = async (payload: {
  warehouseId: number;
  catalogItemId: number;
}) => {
  const { data } = await api.post('/inventory/putaway-rules/resolve', payload);
  return data as PutawayResolveResult | null;
};

export interface PutawaySimulateCandidate {
  ruleId: number;
  matchedOn: PutawayMatchReason;
  priority: number;
  catalogItemId: number | null;
  categoryId: number | null;
  lotTracked: boolean | null;
  destinationLocationId: number;
}

export interface PutawaySimulateResult {
  item: { id: number; categoryId: number | null; lotTracked: boolean };
  candidates: PutawaySimulateCandidate[];
  resolved: PutawayResolveResult | null;
}

export const simulatePutawayRule = async (payload: {
  warehouseId: number;
  catalogItemId: number;
}) => {
  const { data } = await api.post('/inventory/putaway-rules/simulate', payload);
  return data as PutawaySimulateResult;
};

// ─── Inventory Phase 5 — Audit opening-balance import ──────────────────────

export type AuditImportMode = 'auto' | 'catalog' | 'stock';
export type AuditImportResolvedMode = 'catalog' | 'stock';

export interface AuditImportColumnMap {
  partNumber: number | null;
  description: number | null;
  quantity: number | null;
  warehouseCode: number | null;
  unit: number | null;
  unitCost: number | null;
  lotCode: number | null;
  category: number | null;
  vendor: number | null;
  currency: number | null;
}

export interface AuditImportParseSummary {
  mode: AuditImportResolvedMode;
  totalRows: number;
  headerRow: number;
  columnMap: AuditImportColumnMap;
}

export interface AuditImportDictionaryAddition {
  dimension: 'class' | 'item' | 'color' | 'size' | 'supplier' | 'softness';
  code: string;
  label: string;
  sourceRows: number[];
}

export interface AuditImportCatalogItemCandidate {
  partNumber: string;
  description: string;
  unitName?: string;
  categoryName?: string;
  vendorName?: string;
  currencyCode?: string;
  defaultUnitPrice?: number;
  sourceRows: number[];
}

export interface AuditImportStockLineCandidate {
  rowNum: number;
  partNumber: string;
  warehouseCode: string | null;
  quantity: number;
  unitCost: number | null;
  lotCode: string | null;
}

export interface AuditImportRejectedRow {
  rowNum: number;
  partNumber: string;
  reason: string;
}

export interface AuditImportPreviewResult {
  mode: AuditImportResolvedMode;
  summary: AuditImportParseSummary;
  dictionaryAdditions: AuditImportDictionaryAddition[];
  catalogItemsToCreate: AuditImportCatalogItemCandidate[];
  stockLinesToPost: AuditImportStockLineCandidate[];
  rejected: AuditImportRejectedRow[];
  existingCatalogMatches: number;
}

export interface AuditImportApplyResult {
  mode: AuditImportResolvedMode;
  dictionaryAdded: number;
  catalogItemsCreated: number;
  catalogItemErrors: Array<{ row: number; partNumber: string; error: string }>;
  physicalCountId: number | null;
  physicalCountCode: string | null;
  stockMovesPosted: number;
  journalEntryIds: number[];
  errors: Array<{ row: number; partNumber: string; error: string }>;
}

export interface AuditImportRun {
  id: number;
  countCode: string;
  warehouseId: number;
  state: string;
  startDate: string;
  endDate: string | null;
  sourceModule: string | null;
  createdById: number;
  notes: string | null;
  createdAt: string;
  warehouse?: { id: number; code: string; name: string };
  createdBy?: { id: number; displayName: string };
  _count?: { lines: number };
}

export interface AuditImportRunListResult {
  rows: AuditImportRun[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditImportRunDetail extends AuditImportRun {
  lines: Array<{
    id: number;
    catalogItemId: number;
    locationId: number;
    systemQty: string | number;
    countedQty: string | number;
    differenceQty: string | number;
    unitCost: string | number | null;
    adjustmentMoveId: number | null;
    notes: string | null;
    catalogItem?: { id: number; partNumber: string; description: string };
    location?: { id: number; code: string; name: string };
    adjustmentMove?: {
      id: number;
      moveCode: string;
      state: string;
      unitCost: string | number | null;
      totalCost: string | number | null;
      journalEntryId: number | null;
    };
  }>;
}

export const auditImportPreview = async (file: File, mode: AuditImportMode = 'auto') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  const { data } = await api.post('/inventory/audit-import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data as AuditImportPreviewResult;
};

export const auditImportApply = async (
  file: File,
  mode: AuditImportMode = 'auto',
  postOpeningBalance: boolean = true
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  formData.append('postOpeningBalance', String(postOpeningBalance));
  const { data } = await api.post('/inventory/audit-import/apply', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data as AuditImportApplyResult;
};

export const auditImportListRuns = async (params?: { limit?: number; offset?: number }) => {
  const { data } = await api.get('/inventory/audit-import/runs', { params });
  return data as AuditImportRunListResult;
};

export const auditImportGetRun = async (id: number) => {
  const { data } = await api.get(`/inventory/audit-import/runs/${id}`);
  return data as AuditImportRunDetail;
};

// ─── CALENDAR ────────────────────────────────────────────────────────────────
export interface CalendarDepartment {
  id: number;
  code: string;
  label: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
}

export interface CalendarEventType {
  id: number;
  code: string;
  label: string;
  department: string | null;
  color: string;
  sortOrder: number;
  isActive: boolean;
}

export interface CalendarRoom {
  id: number;
  code: string;
  name: string;
  location: string | null;
  capacity: number | null;
  facilities: string | null;
  isActive: boolean;
  createdAt?: string;
}

export interface CalendarEventAttendee {
  id: number;
  eventId: number;
  userId: number;
  role: string;      // organizer | attendee | optional
  response: string;  // pending | accepted | declined | tentative
  createdAt?: string;
  user?: { id: number; displayName: string; email?: string };
}

export interface CalendarEventReminder {
  id: number;
  eventId: number;
  minutesBefore: number;
  channel: string;   // in_app | email | push
  notifyAt: string;
  sent: boolean;
  sentAt: string | null;
}

export interface CalendarEvent {
  id: number;
  eventCode: string;
  departmentId: number;
  eventTypeId: number;
  title: string;
  description: string | null;
  location: string | null;
  start: string;
  end: string | null;
  isAllDay: boolean;
  timezone: string;
  visibility: string; // private | department | company
  status: string;     // scheduled | confirmed | completed | cancelled | rescheduled
  createdById: number;
  sourceModule: string | null;
  sourceId: string | null;
  clientId: number | null;
  dealId: number | null;
  roomId: number | null;
  recurrenceRule: string | null;
  recurrenceParentId: number | null;
  originalStart: string | null;
  cancelledReason: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  department?: CalendarDepartment;
  eventType?: CalendarEventType;
  createdBy?: { id: number; displayName: string; email?: string };
  room?: CalendarRoom | null;
  attendees?: CalendarEventAttendee[];
  reminders?: CalendarEventReminder[];
  _warnings?: Array<{
    type: 'attendee' | 'client' | 'room' | 'overlap';
    existingEventId: number;
    existingEventCode: string;
    existingTitle: string;
    existingStart: string;
    existingEnd: string | null;
    message: string;
    attendeeUserId?: number;
  }>;
}

export interface CalendarEventInput {
  departmentId?: number;
  departmentCode?: string;
  eventTypeId?: number;
  eventTypeCode?: string;
  title: string;
  description?: string;
  location?: string;
  start: string;
  end?: string;
  isAllDay?: boolean;
  visibility?: string;
  clientId?: number;
  dealId?: number;
  roomId?: number;
  attendeeUserIds?: number[];
  reminderMinutes?: number[];
  recurrenceRule?: string;
}

export interface CalendarConflictError {
  error: string;
  existingEventId: number;
  conflictType: 'client' | 'room';
  message: string;
}

export interface CalendarListParams {
  dateFrom?: string;
  dateTo?: string;
  departmentCode?: string;
  eventTypeCode?: string;
  attendeeUserId?: number;
  view?: 'client_appointments' | 'my_department' | 'company' | 'mine';
  status?: string;
  clientId?: number;
  dealId?: number;
  page?: number;
  limit?: number;
}

export const calendarListEvents = async (params?: CalendarListParams) => {
  const { data } = await api.get('/calendar/events', { params });
  return (data?.events ?? []) as CalendarEvent[];
};

export const calendarGetCalendarView = async (params: {
  dateFrom: string;
  dateTo: string;
  view?: string;
  departmentCode?: string;
}) => {
  const { data } = await api.get('/calendar/events/calendar', { params });
  return (data?.events ?? []) as CalendarEvent[];
};

export const calendarGetEvent = async (id: number) => {
  const { data } = await api.get(`/calendar/events/${id}`);
  return data as CalendarEvent;
};

export const calendarCreateEvent = async (data: CalendarEventInput) => {
  const res = await api.post('/calendar/events', data);
  return res.data as CalendarEvent;
};

export const calendarUpdateEvent = async (id: number, data: Partial<CalendarEventInput>) => {
  const res = await api.put(`/calendar/events/${id}`, data);
  return res.data as CalendarEvent;
};

export const calendarConfirmEvent = async (id: number) => {
  const { data } = await api.post(`/calendar/events/${id}/confirm`);
  return data as CalendarEvent;
};

export const calendarCompleteEvent = async (id: number) => {
  const { data } = await api.post(`/calendar/events/${id}/complete`);
  return data as CalendarEvent;
};

export const calendarCancelEvent = async (id: number, reason?: string) => {
  const { data } = await api.post(`/calendar/events/${id}/cancel`, { reason });
  return data as CalendarEvent;
};

export const calendarReplaceEvent = async (oldEventId: number, newEventData: CalendarEventInput) => {
  const { data } = await api.post('/calendar/events/replace', { oldEventId, newEventData });
  return data as { oldEvent: CalendarEvent; newEvent: CalendarEvent };
};

export const calendarListDepartments = async () => {
  const { data } = await api.get('/calendar/departments');
  return (data?.departments ?? []) as CalendarDepartment[];
};

export const calendarListEventTypes = async () => {
  const { data } = await api.get('/calendar/event-types');
  return (data?.eventTypes ?? []) as CalendarEventType[];
};

export const calendarListRooms = async () => {
  const { data } = await api.get('/calendar/rooms');
  return (data?.rooms ?? []) as CalendarRoom[];
};

export const calendarGetRoom = async (id: number) => {
  const { data } = await api.get(`/calendar/rooms/${id}`);
  return data as CalendarRoom;
};

export const calendarGetRoomAvailability = async (
  roomId: number,
  params: { dateFrom: string; dateTo: string }
) => {
  const { data } = await api.get(`/calendar/rooms/${roomId}/availability`, { params });
  return (data?.busy ?? []) as Array<{ eventId: number; title: string; start: string; end: string | null }>;
};

export const calendarCreateRoom = async (data: Partial<CalendarRoom>) => {
  const res = await api.post('/calendar/rooms', data);
  return res.data as CalendarRoom;
};

export const calendarUpdateRoom = async (id: number, data: Partial<CalendarRoom>) => {
  const res = await api.put(`/calendar/rooms/${id}`, data);
  return res.data as CalendarRoom;
};

// ─────────────────────────────────────────────────────────────────────────────
// Inventory Phase 7 — Landed Cost + FX Revaluation + Valuation Layers
// ─────────────────────────────────────────────────────────────────────────────

export type LandedCostSplitMethod =
  | 'by_value'
  | 'by_qty'
  | 'by_weight'
  | 'by_volume'
  | 'manual';

export type LandedCostState = 'draft' | 'posted' | 'cancelled';

export interface LandedCostLine {
  id: number;
  stockMoveId: number;
  amount: string;
  accountId: number;
  costCenterId: number;
  stockMove?: {
    id: number;
    moveCode: string;
    quantity: string;
    unitCost: string;
    totalCost: string;
    sourceModule: string | null;
    sourceId: string | null;
    catalogItem?: {
      id: number;
      partNumber: string;
      description: string;
      materialClass: string | null;
    };
  };
  account?: { id: number; code: string; name: string };
  costCenter?: { id: number; code: string; name: string };
}

export interface LandedCostHeader {
  id: number;
  code: string;
  totalCost: string;
  splitMethod: LandedCostSplitMethod;
  state: LandedCostState;
  notes: string | null;
  vendorBillJournalEntryId: number | null;
  appliedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: LandedCostLine[];
  createdBy?: { id: number; name: string };
  vendorBillJournalEntry?: {
    id: number;
    entryCode: string;
    state: string;
    amountTotal: string;
  } | null;
}

export const listLandedCosts = async (state?: LandedCostState) => {
  const { data } = await api.get('/landed-costs', {
    params: state ? { state } : undefined,
  });
  return data as LandedCostHeader[];
};

export const getLandedCost = async (id: number) => {
  const { data } = await api.get(`/landed-costs/${id}`);
  return data as LandedCostHeader;
};

export const createLandedCost = async (payload: {
  code?: string;
  vendorBillJournalEntryId?: number | null;
  totalCost: number;
  splitMethod: LandedCostSplitMethod;
  notes?: string | null;
  lines: Array<{
    stockMoveId: number;
    amount: number;
    accountId: number;
    costCenterId: number;
  }>;
}) => {
  const { data } = await api.post('/landed-costs', payload);
  return data as LandedCostHeader;
};

export const postLandedCost = async (id: number) => {
  const { data } = await api.post(`/landed-costs/${id}/post`);
  return data as { header: LandedCostHeader; journalEntryId: number };
};

export const cancelLandedCost = async (id: number, reason?: string) => {
  const { data } = await api.post(`/landed-costs/${id}/cancel`, { reason });
  return data as LandedCostHeader;
};

export const prorateLandedCostPreview = async (payload: {
  moves: Array<{ stockMoveId: number; basis: number; manualAmount?: number }>;
  totalCost: number;
  splitMethod: LandedCostSplitMethod;
}) => {
  const { data } = await api.post('/landed-costs/prorate', payload);
  return data as { slices: Array<{ stockMoveId: number; amount: number }> };
};

export interface RevaluationLineRow {
  valuationLayerId: number;
  catalogItemId: number;
  catalogItemCode: string | null;
  catalogItemDescription: string | null;
  stockMoveId: number;
  moveCode: string;
  grnCode: string | null;
  poCode: string | null;
  vendorName: string | null;
  originalCurrencyCode: string | null;
  originalForeignAmount: number | null;
  originalRate: number;
  currentRate: number;
  originalEgpValue: number;
  newEgpValue: number;
  delta: number;
  direction: 'up' | 'down' | 'flat';
  kind: 'gain' | 'loss' | 'none';
  skippedNoForeignCurrency: boolean;
}

export interface RevaluationPreview {
  asOfDate: string;
  totalLayers: number;
  revaluedLayers: number;
  skippedLayers: number;
  totalGainEgp: number;
  totalLossEgp: number;
  netDeltaEgp: number;
  rows: RevaluationLineRow[];
  warning: string;
  anchors?: any;
}

export const previewRevaluation = async (payload: {
  asOfDate: string;
  warehouseId?: number;
  catalogItemId?: number;
}) => {
  const { data } = await api.post('/inventory/revaluation/preview', payload);
  return data as RevaluationPreview;
};

export const postRevaluationDraft = async (payload: {
  asOfDate: string;
  warehouseId?: number;
  catalogItemId?: number;
}) => {
  const { data } = await api.post('/inventory/revaluation/draft', payload);
  return data as { journalEntryId: number; entryCode: string; preview: RevaluationPreview };
};

export interface ValuationLayerRow {
  id: number;
  catalogItemId: number;
  stockMoveId: number;
  accountingDate: string;
  quantity: string;
  remainingQuantity: string;
  unitCost: string;
  value: string;
  remainingValue: string;
  originalUnitCostFc: string | null;
  originalExchangeRate: string | null;
  catalogItem?: {
    id: number;
    partNumber: string;
    description: string;
    materialClass: string | null;
  };
  companyCurrency?: { id: number; code: string };
  originalCurrency?: { id: number; code: string } | null;
  stockMove?: {
    id: number;
    moveCode: string;
    moveType: string;
    sourceModule: string | null;
    sourceId: string | null;
    doneDate: string | null;
  };
}

export const listValuationLayers = async (params?: {
  catalogItemId?: number;
  onlyOpen?: boolean;
}) => {
  const { data } = await api.get('/inventory/revaluation/valuation-layers', {
    params,
  });
  return data as ValuationLayerRow[];
};

// ─── Accounting Phase 12 — Fiscal Year & Lock Dates ──────────────────────────

export interface FiscalYearRow {
  id: number;
  name: string;
  dateFrom: string;
  dateTo: string;
  isActive: boolean;
  isClosed: boolean;
  closedAt: string | null;
  closedById: number | null;
  dateLockLastClose: string | null;
  closingEntryId: number | null;
  openingEntryId: number | null;
  createdAt: string;
  closedBy?: { id: number; displayName: string } | null;
  closingEntry?: { id: number; entryCode: string; state: string } | null;
  openingEntry?: { id: number; entryCode: string; state: string } | null;
}

export interface FiscalYearPreviewRow {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType: string;
  debit: number;
  credit: number;
  balance: number;
  closingSide: 'Dr' | 'Cr' | 'none';
  closingAmount: number;
}

export interface FiscalYearPreview {
  fiscalYear: FiscalYearRow;
  draftEntriesBlocking: number;
  readyToClose: boolean;
  rows: FiscalYearPreviewRow[];
  totalIncome: number;
  totalExpense: number;
  netIncome: number;
  retainedEarningsImpact: { side: 'Dr' | 'Cr'; amount: number };
}

export interface LockDatesRow {
  id: number;
  taxLockDate: string | null;
  fiscalLockDate: string | null;
  advisorLockDate: string | null;
  updatedAt: string;
  updatedById: number | null;
  updatedBy: { id: number; displayName: string } | null;
  note: string | null;
}

export const listFiscalYears = async () => {
  const { data } = await api.get('/fiscal-periods/years');
  return data as FiscalYearRow[];
};

export const listOpenFiscalYears = async () => {
  const { data } = await api.get('/fiscal-periods/years/open');
  return data as FiscalYearRow[];
};

export const getFiscalYear = async (id: number) => {
  const { data } = await api.get(`/fiscal-periods/years/${id}`);
  return data as FiscalYearRow;
};

export const createFiscalYear = async (payload: {
  name: string;
  dateFrom: string;
  dateTo: string;
  isActive?: boolean;
}) => {
  const { data } = await api.post('/fiscal-periods/years', payload);
  return data as FiscalYearRow;
};

export const createCalendarFiscalYear = async (year: number) => {
  const { data } = await api.post('/fiscal-periods/years/calendar', { year });
  return data as FiscalYearRow;
};

export const previewFiscalYearClose = async (id: number) => {
  const { data } = await api.get(`/fiscal-periods/years/${id}/preview`);
  return data as FiscalYearPreview;
};

export const closeFiscalYear = async (id: number) => {
  const { data } = await api.post(`/fiscal-periods/years/${id}/close`);
  return data as {
    fiscalYear: FiscalYearRow;
    closingEntry: { id: number; entryCode: string };
    netIncome: number;
    totalIncome: number;
    totalExpense: number;
  };
};

export const getLockDates = async () => {
  const { data } = await api.get('/fiscal-periods/lock-dates');
  return data as LockDatesRow;
};

export const setLockDates = async (payload: {
  taxLockDate?: string | null;
  fiscalLockDate?: string | null;
  advisorLockDate?: string | null;
  note?: string | null;
}) => {
  const { data } = await api.post('/fiscal-periods/lock-dates', payload);
  return data as LockDatesRow;
};

export const checkLockDate = async (date: string) => {
  const { data } = await api.post('/fiscal-periods/lock-dates/check', { date });
  return data as {
    locked: boolean;
    kind: 'ok' | 'tax_locked' | 'fiscal_locked' | 'advisor_locked';
    message: string;
  };
};

// ─── Inventory Phase 8 — Physical Counts, ABC, Cycle Counts, Approval Rules ──

export const listPhysicalCounts = async (filter?: { warehouseId?: number; state?: string; limit?: number; offset?: number }) => {
  const { data } = await api.get('/inventory/physical-counts', { params: filter });
  return data;
};

export const getPhysicalCount = async (id: number) => {
  const { data } = await api.get(`/inventory/physical-counts/${id}`);
  return data;
};

export const createPhysicalCount = async (payload: { warehouseId: number; startDate?: string; notes?: string }) => {
  const { data } = await api.post('/inventory/physical-counts', payload);
  return data;
};

export const populateCountLines = async (countId: number) => {
  const { data } = await api.post(`/inventory/physical-counts/${countId}/populate`);
  return data;
};

export const addCountLine = async (countId: number, payload: { catalogItemId: number; locationId: number; lotId?: number; notes?: string }) => {
  const { data } = await api.post(`/inventory/physical-counts/${countId}/lines`, payload);
  return data;
};

export const recordCountLine = async (lineId: number, countedQty: number) => {
  const { data } = await api.post(`/inventory/physical-counts/lines/${lineId}/record`, { countedQty });
  return data;
};

export const updateCountLine = async (lineId: number, payload: { countedQty?: number; notes?: string }) => {
  const { data } = await api.patch(`/inventory/physical-counts/lines/${lineId}`, payload);
  return data;
};

export const deleteCountLine = async (lineId: number) => {
  const { data } = await api.delete(`/inventory/physical-counts/lines/${lineId}`);
  return data;
};

export const startPhysicalCount = async (id: number) => {
  const { data } = await api.post(`/inventory/physical-counts/${id}/start`);
  return data;
};

export const closePhysicalCount = async (id: number) => {
  const { data } = await api.post(`/inventory/physical-counts/${id}/close`);
  return data;
};

export const cancelPhysicalCount = async (id: number) => {
  const { data } = await api.post(`/inventory/physical-counts/${id}/cancel`);
  return data;
};

// ABC Classification

export const runAbcClassification = async (opts?: { warehouseId?: number; monthsBack?: number }) => {
  const { data } = await api.post('/inventory/abc/run', opts ?? {});
  return data;
};

export const getAbcReport = async (opts?: { warehouseId?: number }) => {
  const { data } = await api.get('/inventory/abc/report', { params: opts });
  return data;
};

export const getAbcSummary = async () => {
  const { data } = await api.get('/inventory/abc/summary');
  return data;
};

// Cycle Count Policies

export const listCycleCountPolicies = async (filter?: { warehouseId?: number; isActive?: boolean }) => {
  const { data } = await api.get('/inventory/cycle-counts/policies', { params: filter });
  return data;
};

export const getCycleCountPolicy = async (id: number) => {
  const { data } = await api.get(`/inventory/cycle-counts/policies/${id}`);
  return data;
};

export const createCycleCountPolicy = async (payload: { abcClass: string; frequencyDays: number; coverageTargetPct: number; warehouseId?: number }) => {
  const { data } = await api.post('/inventory/cycle-counts/policies', payload);
  return data;
};

export const updateCycleCountPolicy = async (id: number, payload: Record<string, unknown>) => {
  const { data } = await api.patch(`/inventory/cycle-counts/policies/${id}`, payload);
  return data;
};

export const deleteCycleCountPolicy = async (id: number) => {
  const { data } = await api.delete(`/inventory/cycle-counts/policies/${id}`);
  return data;
};

export const runCycleCountScheduler = async () => {
  const { data } = await api.post('/inventory/cycle-counts/run');
  return data;
};

// Approval Rules

export const listApprovalRules = async (filter?: { actionType?: string; isActive?: boolean }) => {
  const { data } = await api.get('/inventory/approval-rules', { params: filter });
  return data;
};

export const getApprovalRule = async (id: number) => {
  const { data } = await api.get(`/inventory/approval-rules/${id}`);
  return data;
};

export const createApprovalRule = async (payload: { actionType: string; thresholdAmount?: number; requiredRoles?: string[]; requiresCeo?: boolean }) => {
  const { data } = await api.post('/inventory/approval-rules', payload);
  return data;
};

export const updateApprovalRule = async (id: number, payload: Record<string, unknown>) => {
  const { data } = await api.patch(`/inventory/approval-rules/${id}`, payload);
  return data;
};

export const deleteApprovalRule = async (id: number) => {
  const { data } = await api.delete(`/inventory/approval-rules/${id}`);
  return data;
};

export const checkApproval = async (actionType: string, amount: number) => {
  const { data } = await api.post('/inventory/approval-rules/check', { actionType, amount });
  return data as { approved: boolean; reason?: string };
};

// ─── Inventory Phase 9 — Kits, Consignment, Dropship, In-Transit ─────────────

export const listKits = async (filter?: { isActive?: boolean; isPhantom?: boolean }) => {
  const { data } = await api.get('/inventory/kits', { params: filter });
  return data;
};

export const getKit = async (id: number) => {
  const { data } = await api.get(`/inventory/kits/${id}`);
  return data;
};

export const createKit = async (payload: { kitCatalogItemId: number; description?: string; isPhantom?: boolean; components: Array<{ componentCatalogItemId: number; quantity: number; uomId: number }> }) => {
  const { data } = await api.post('/inventory/kits', payload);
  return data;
};

export const updateKit = async (id: number, payload: Record<string, unknown>) => {
  const { data } = await api.patch(`/inventory/kits/${id}`, payload);
  return data;
};

export const addKitComponent = async (kitId: number, payload: { componentCatalogItemId: number; quantity: number; uomId: number }) => {
  const { data } = await api.post(`/inventory/kits/${kitId}/components`, payload);
  return data;
};

export const updateKitComponent = async (componentId: number, payload: { quantity?: number; uomId?: number }) => {
  const { data } = await api.patch(`/inventory/kits/components/${componentId}`, payload);
  return data;
};

export const removeKitComponent = async (componentId: number) => {
  const { data } = await api.delete(`/inventory/kits/components/${componentId}`);
  return data;
};

export const assembleKit = async (kitId: number, payload: { quantity: number; warehouseId: number }) => {
  const { data } = await api.post(`/inventory/kits/${kitId}/assemble`, payload);
  return data;
};

export const disassembleKit = async (kitId: number, payload: { quantity: number; warehouseId: number }) => {
  const { data } = await api.post(`/inventory/kits/${kitId}/disassemble`, payload);
  return data;
};

// Consignment

export const listConsignmentStock = async (warehouseId?: number) => {
  const { data } = await api.get('/inventory/consignment/stock', { params: warehouseId ? { warehouseId } : {} });
  return data;
};

export const createConsignmentReceipt = async (payload: { catalogItemId: number; quantity: number; uomId: number; vendorId: number; warehouseId: number; unitCost?: number; lotReference?: string; notes?: string }) => {
  const { data } = await api.post('/inventory/consignment/receive', payload);
  return data;
};

export const consumeConsignment = async (payload: { catalogItemId: number; quantity: number; locationId: number; warehouseId: number; notes?: string }) => {
  const { data } = await api.post('/inventory/consignment/consume', payload);
  return data;
};

// Dropship

export const listDropshipMoves = async (filter?: { warehouseId?: number; limit?: number; offset?: number }) => {
  const { data } = await api.get('/inventory/dropship', { params: filter });
  return data;
};

export const createDropshipMove = async (payload: { catalogItemId: number; quantity: number; uomId: number; warehouseId: number; unitCost: number; referenceDoc?: string; notes?: string }) => {
  const { data } = await api.post('/inventory/dropship', payload);
  return data;
};

// In-Transit

export const listInTransitTransfers = async () => {
  const { data } = await api.get('/inventory/in-transit');
  return data;
};

export const createInTransitTransfer = async (payload: { catalogItemId: number; quantity: number; uomId: number; sourceWarehouseId: number; destWarehouseId: number; lotId?: number; notes?: string }) => {
  const { data } = await api.post('/inventory/in-transit', payload);
  return data;
};

export const receiveInTransit = async (moveId: number, receivedQty: number, notes?: string) => {
  const { data } = await api.post(`/inventory/in-transit/${moveId}/receive`, { receivedQty, notes });
  return data;
};

// ─── Inventory Phase 10 — RMA ────────────────────────────────────────────────

export const listRmas = async (filter?: { vendorId?: number; state?: string; limit?: number; offset?: number }) => {
  const { data } = await api.get('/inventory/rmas', { params: filter });
  return data;
};

export const getRma = async (id: number) => {
  const { data } = await api.get(`/inventory/rmas/${id}`);
  return data;
};

export const createRma = async (payload: { vendorId: number; reason?: string; notes?: string; lines: Array<{ grnLineId?: number; catalogItemId: number; quantity: number; lotId?: number; serialNumberId?: number; unitCost: number }> }) => {
  const { data } = await api.post('/inventory/rmas', payload);
  return data;
};

export const authorizeRma = async (id: number, authorizationNumber?: string) => {
  const { data } = await api.post(`/inventory/rmas/${id}/authorize`, { authorizationNumber });
  return data;
};

export const shipRma = async (id: number, shippingDate?: string) => {
  const { data } = await api.post(`/inventory/rmas/${id}/ship`, { shippingDate });
  return data;
};

export const receiveRma = async (id: number, creditAmount?: number) => {
  const { data } = await api.post(`/inventory/rmas/${id}/receive`, { creditAmount });
  return data;
};

export const closeRma = async (id: number) => {
  const { data } = await api.post(`/inventory/rmas/${id}/close`);
  return data;
};

export const cancelRma = async (id: number) => {
  const { data } = await api.post(`/inventory/rmas/${id}/cancel`);
  return data;
};

// ─── Accounting Phase 13 — AR Collection Levels & Overdue Management ─────────

export const getCollectionLevels = async (includeInactive?: boolean) => {
  const { data } = await api.get('/accounting/collections/levels', {
    params: includeInactive ? { includeInactive: 'true' } : {},
  });
  return data;
};

export const createCollectionLevel = async (payload: {
  name: string;
  sequence: number;
  delayDays: number;
  description?: string;
  sendEmail?: boolean;
  sendLetter?: boolean;
  manualAction?: boolean;
  joinInvoices?: boolean;
}) => {
  const { data } = await api.post('/accounting/collections/levels', payload);
  return data;
};

export const updateCollectionLevel = async (id: number, payload: Record<string, any>) => {
  const { data } = await api.put(`/accounting/collections/levels/${id}`, payload);
  return data;
};

export const getCollectionPartners = async (asOfDate?: string) => {
  const { data } = await api.get('/accounting/collections/partners', {
    params: asOfDate ? { asOfDate } : {},
  });
  return data;
};

export const executeCollection = async (payload: {
  partnerId: number;
  levelId: number;
  note?: string;
  sendEmail?: boolean;
}) => {
  const { data } = await api.post('/accounting/collections/execute', payload);
  return data;
};

export const bulkExecuteCollection = async (payload: {
  partnerIds: number[];
  levelId: number;
  note?: string;
}) => {
  const { data } = await api.post('/accounting/collections/execute-bulk', payload);
  return data;
};

export const getCollectionReport = async () => {
  const { data } = await api.get('/accounting/collections/report');
  return data;
};

export const getPartnerCollectionHistory = async (partnerId: number) => {
  const { data } = await api.get(`/accounting/collections/history/${partnerId}`);
  return data;
};

// ─── Accounting Phase 15 — Fixed Assets & Depreciation ─────────────────────

export const listAssetCategories = async () => {
  const { data } = await api.get('/fixed-assets/categories');
  return data;
};

export const createAssetCategory = async (payload: any) => {
  const { data } = await api.post('/fixed-assets/categories', payload);
  return data;
};

export const updateAssetCategory = async (id: number, payload: any) => {
  const { data } = await api.put(`/fixed-assets/categories/${id}`, payload);
  return data;
};

export const listAssets = async (params?: any) => {
  const { data } = await api.get('/fixed-assets', { params });
  return data;
};

export const getAsset = async (id: number) => {
  const { data } = await api.get(`/fixed-assets/${id}`);
  return data;
};

export const createAsset = async (payload: any) => {
  const { data } = await api.post('/fixed-assets', payload);
  return data;
};

export const confirmAsset = async (id: number) => {
  const { data } = await api.post(`/fixed-assets/${id}/confirm`);
  return data;
};

export const postAssetDepreciation = async (id: number, upToDate?: string) => {
  const { data } = await api.post(`/fixed-assets/${id}/depreciate`, { upToDate });
  return data;
};

export const postAllDepreciations = async (upToDate?: string) => {
  const { data } = await api.post('/fixed-assets/depreciate-all', { upToDate });
  return data;
};

export const disposeAsset = async (id: number, payload: { disposalDate: string; saleAmount: number }) => {
  const { data } = await api.post(`/fixed-assets/${id}/dispose`, payload);
  return data;
};

export const revalueAsset = async (id: number, newBookValue: number) => {
  const { data } = await api.post(`/fixed-assets/${id}/revalue`, { newBookValue });
  return data;
};

export const previewDepreciationSchedule = async (payload: any) => {
  const { data } = await api.post('/fixed-assets/preview-schedule', payload);
  return data;
};

export const getAssetRegister = async (params?: any) => {
  const { data } = await api.get('/fixed-assets/register', { params });
  return data;
};

// ── Accounting Phase 16: Deferred Revenue & Expenses ──────────────────────

export const listDeferredEntries = async (params?: any) => {
  const { data } = await api.get('/deferred-entries', { params });
  return data;
};

export const getDeferredEntry = async (id: number) => {
  const { data } = await api.get(`/deferred-entries/${id}`);
  return data;
};

export const getDeferredSummary = async () => {
  const { data } = await api.get('/deferred-entries/summary');
  return data;
};

export const createDeferredEntry = async (payload: any) => {
  const { data } = await api.post('/deferred-entries', payload);
  return data;
};

export const confirmDeferredEntry = async (id: number) => {
  const { data } = await api.post(`/deferred-entries/${id}/confirm`);
  return data;
};

export const postDeferredRecognition = async (id: number, upToDate?: string) => {
  const { data } = await api.post(`/deferred-entries/${id}/recognize`, { upToDate });
  return data;
};

export const postAllDeferredRecognitions = async (upToDate?: string) => {
  const { data } = await api.post('/deferred-entries/recognize-all', { upToDate });
  return data;
};

export const previewDeferredSchedule = async (payload: any) => {
  const { data } = await api.post('/deferred-entries/preview-schedule', payload);
  return data;
};

// ─── Accounting Phase 18: Audit Compliance ──────────────────────────────────

export const getAuditComplianceReport = async () => {
  const { data } = await api.get('/audit-compliance/report');
  return data;
};
export const getAuditJournalHashStatus = async () => {
  const { data } = await api.get('/audit-compliance/journals');
  return data;
};
export const verifyAuditHashChain = async (journalId: number) => {
  const { data } = await api.get(`/audit-compliance/verify/${journalId}`);
  return data;
};
export const verifyAuditAllHashChains = async () => {
  const { data } = await api.get('/audit-compliance/verify-all');
  return data;
};
export const setAuditJournalHashMode = async (journalId: number, enabled: boolean) => {
  const { data } = await api.patch(`/audit-compliance/journals/${journalId}/hash-mode`, { enabled });
  return data;
};
export const getAuditEntryHashDetail = async (entryId: number) => {
  const { data } = await api.get(`/audit-compliance/entry/${entryId}`);
  return data;
};

// ─── Accounting Phase 19: Bridge Health ───────────────────────────────────────

export const getBridgeHealthDashboard = async () => {
  const { data } = await api.get('/bridge-health/dashboard');
  return data;
};
export const listBridgeFailures = async (params?: { resolved?: boolean; sourceModule?: string; page?: number; pageSize?: number }) => {
  const { data } = await api.get('/bridge-health/failures', { params });
  return data;
};
export const retryBridgeFailure = async (failureId: number) => {
  const { data } = await api.post(`/bridge-health/failures/${failureId}/retry`);
  return data;
};
export const retryAllBridgeFailures = async () => {
  const { data } = await api.post('/bridge-health/retry-all');
  return data;
};
export const reconcileGRNI = async () => {
  const { data } = await api.get('/bridge-health/reconcile-grni');
  return data;
};
export const reconcileInventory = async () => {
  const { data } = await api.get('/bridge-health/reconcile-inventory');
  return data;
};
export const getOrphanReturns = async () => {
  const { data } = await api.get('/bridge-health/orphan-returns');
  return data;
};
export const runIntegrityChecks = async () => {
  const { data } = await api.get('/bridge-health/integrity-checks');
  return data;
};

// ─── Inventory Phase 11: Demand Forecasting & MRP ───────────────────────────

// Forecasts
export const listForecasts = (params?: any) => api.get('/inventory/forecasts', { params }).then(r => r.data);
export const getForecastDetail = (catalogItemId: number, warehouseId: number, horizonMonths?: number) =>
  api.get('/inventory/forecasts/detail', { params: { catalogItemId, warehouseId, horizonMonths } }).then(r => r.data);
export const runForecast = (payload: { catalogItemId: number; warehouseId: number; method: string; horizonMonths?: number }) =>
  api.post('/inventory/forecasts/run', payload).then(r => r.data);
export const getConsumptionHistory = (catalogItemId: number, warehouseId: number, monthsBack?: number) =>
  api.get('/inventory/forecasts/consumption', { params: { catalogItemId, warehouseId, monthsBack } }).then(r => r.data);

// MRP
export const triggerMrpRun = (scope?: any) => api.post('/inventory/mrp/run', { scope }).then(r => r.data);
export const listMrpRuns = (params?: any) => api.get('/inventory/mrp/runs', { params }).then(r => r.data);
export const getMrpRun = (id: number) => api.get(`/inventory/mrp/runs/${id}`).then(r => r.data);
export const listMrpProposals = (params?: any) => api.get('/inventory/mrp/proposals', { params }).then(r => r.data);
export const confirmMrpProposal = (id: number) => api.post(`/inventory/mrp/proposals/${id}/confirm`).then(r => r.data);
export const rejectMrpProposal = (id: number, reason?: string) => api.post(`/inventory/mrp/proposals/${id}/reject`, { reason }).then(r => r.data);
export const convertMrpProposal = (id: number, departmentId: number) => api.post(`/inventory/mrp/proposals/${id}/convert`, { departmentId }).then(r => r.data);
export const batchConvertMrpProposals = (proposalIds: number[], departmentId: number) =>
  api.post('/inventory/mrp/proposals/batch-convert', { proposalIds, departmentId }).then(r => r.data);
export const getProjectedOnHand = (catalogItemId: number, warehouseId: number, horizonDays?: number) =>
  api.get('/inventory/mrp/projected', { params: { catalogItemId, warehouseId, horizonDays } }).then(r => r.data);

// Reorder Alerts
export const listReorderAlerts = (params?: any) => api.get('/inventory/reorder-alerts', { params }).then(r => r.data);
export const getReorderAlertCount = (warehouseId?: number) =>
  api.get('/inventory/reorder-alerts/count', { params: { warehouseId } }).then(r => r.data);

// Reorder Rules
export const listReorderRules = (params?: any) => api.get('/inventory/reorder-rules', { params }).then(r => r.data);
export const getReorderRule = (id: number) => api.get(`/inventory/reorder-rules/${id}`).then(r => r.data);
export const createReorderRule = (payload: any) => api.post('/inventory/reorder-rules', payload).then(r => r.data);
export const updateReorderRule = (id: number, payload: any) => api.patch(`/inventory/reorder-rules/${id}`, payload).then(r => r.data);
export const deleteReorderRule = (id: number) => api.delete(`/inventory/reorder-rules/${id}`).then(r => r.data);

// ── CRM Follow-Up Admin API (Phase 5A) ──────────────────────────────────────

// Rules
export const crmListFollowUpRules = async () => {
  const { data } = await api.get('/crm/admin/follow-up-rules');
  return data;
};
export const crmCreateFollowUpRule = async (rule: Record<string, any>) => {
  const { data } = await api.post('/crm/admin/follow-up-rules', rule);
  return data;
};
export const crmUpdateFollowUpRule = async (id: number, updates: Record<string, any>) => {
  const { data } = await api.patch(`/crm/admin/follow-up-rules/${id}`, updates);
  return data;
};
export const crmSimulateFollowUpRule = async (id: number) => {
  const { data } = await api.post(`/crm/admin/follow-up-rules/${id}/simulate`);
  return data;
};
export const crmRunFollowUpRuleNow = async (id: number) => {
  const { data } = await api.post(`/crm/admin/follow-up-rules/${id}/run-now`);
  return data;
};
export const crmGetFollowUpObservability = async () => {
  const { data } = await api.get('/crm/admin/follow-up-rules/observability');
  return data;
};
export const crmSeedFollowUpRules = async () => {
  const { data } = await api.post('/crm/admin/follow-up-rules/seed');
  return data;
};

// Settings
export const crmGetFollowUpSettings = async () => {
  const { data } = await api.get('/crm/admin/follow-up-settings');
  return data;
};
export const crmUpdateFollowUpSettings = async (updates: Record<string, any>) => {
  const { data } = await api.patch('/crm/admin/follow-up-settings', updates);
  return data;
};

// Business Hours
export const crmGetBusinessHours = async () => {
  const { data } = await api.get('/crm/admin/business-hours');
  return data;
};
export const crmUpdateBusinessHours = async (rows: any[]) => {
  const { data } = await api.put('/crm/admin/business-hours', { rows });
  return data;
};

// Holidays
export const crmListBusinessHolidays = async () => {
  const { data } = await api.get('/crm/admin/business-holidays');
  return data;
};
export const crmAddBusinessHoliday = async (holidayDate: string, label: string) => {
  const { data } = await api.post('/crm/admin/business-holidays', { holidayDate, label });
  return data;
};
export const crmDeleteBusinessHoliday = async (id: number) => {
  const { data } = await api.delete(`/crm/admin/business-holidays/${id}`);
  return data;
};

// Stage SLA
export const crmGetStageSla = async () => {
  const { data } = await api.get('/crm/admin/stage-sla');
  return data;
};
export const crmUpdateStageSla = async (rows: any[]) => {
  const { data } = await api.put('/crm/admin/stage-sla', { rows });
  return data;
};

// AM Task Actions
export const crmSnoozeTask = async (taskId: number, until: string) => {
  const { data } = await api.post(`/crm/tasks/${taskId}/snooze`, { until });
  return data;
};
export const crmLogTaskOutcome = async (taskId: number, outcome: string, notes?: string) => {
  const { data } = await api.post(`/crm/tasks/${taskId}/log-outcome`, { outcome, notes });
  return data;
};

// AR Collection Bridge (Phase 5B)
export const crmListArCollections = async (params?: Record<string, any>) => {
  const { data } = await api.get('/crm/ar-collections', { params });
  return data;
};
export const crmUpdateArCollectionStatus = async (taskId: number, status: string, notes?: string) => {
  const { data } = await api.patch(`/crm/ar-collections/${taskId}/status`, { status, notes });
  return data;
};
export const crmGetArCollectionSummary = async () => {
  const { data } = await api.get('/crm/ar-collections/summary');
  return data;
};

// Client Message Outbox (Phase 6)
export const crmListClientMessages = async (params?: Record<string, any>) => {
  const { data } = await api.get('/crm/client-messages', { params });
  return data;
};
export const crmGetClientMessage = async (id: number) => {
  const { data } = await api.get(`/crm/client-messages/${id}`);
  return data;
};
export const crmEditClientMessage = async (id: number, body: string, subject?: string) => {
  const { data } = await api.patch(`/crm/client-messages/${id}`, { body, subject });
  return data;
};
export const crmApproveClientMessage = async (id: number) => {
  const { data } = await api.post(`/crm/client-messages/${id}/approve`);
  return data;
};
export const crmCancelClientMessage = async (id: number) => {
  const { data } = await api.post(`/crm/client-messages/${id}/cancel`);
  return data;
};
export const crmRetryClientMessage = async (id: number) => {
  const { data } = await api.post(`/crm/client-messages/${id}/retry`);
  return data;
};

export default api;

// ═══════════════════════════════════════════════════════════════════════════════
// Cost allocation (cost centers on MR / PO lines) — 2026-09-05
// ═══════════════════════════════════════════════════════════════════════════════

export interface CostCenterRef {
  id: number;
  code: string;
  name: string;
  typeCode?: string;
  isActive?: boolean;
  parent?: CostCenterRef | null; // ancestors chain (server sends up to 4 levels up)
}

export interface CostCenterRow extends CostCenterRef {
  typeCode: string;
  label: string;
  externalRef?: string | null;
  notes?: string | null;
  sortOrder: number;
  source: string;
  departmentId?: number | null;
  childCount: number;
  usage: number;
  depth?: number;
  type: { code: string; label: string; parentTypeCode: string | null; childRequired: boolean; anyParent?: boolean; sortOrder: number };
  createdAt?: string;
  updatedAt?: string;
}

export interface CostCenterType {
  code: string;
  label: string;
  parentTypeCode: string | null;
  childRequired: boolean;
  anyParent?: boolean;
  isActive: boolean;
  sortOrder: number;
}

/** Root → node chain */
export const costCenterChain = (cc?: CostCenterRef | null): CostCenterRef[] => {
  const out: CostCenterRef[] = [];
  let cur: CostCenterRef | null | undefined = cc;
  while (cur) { out.unshift(cur); cur = cur.parent; }
  return out;
};

export const costCenterLabel = (cc?: CostCenterRef | null): string => costCenterChain(cc).map((n) => n.name).join(' › ');

export const listAllocCostCenters = async (all = false): Promise<CostCenterRow[]> => {
  const { data } = await api.get('/cost-allocation', { params: all ? { all: 1 } : undefined });
  return data;
};

export const listCostCenterTypes = async (): Promise<CostCenterType[]> => {
  const { data } = await api.get('/cost-allocation/types');
  return data;
};

export const getCostCenterConfig = async (): Promise<{ requiredFrom: string | null; requiredNow: boolean }> => {
  const { data } = await api.get('/cost-allocation/config');
  return data;
};

export const setCostCenterConfig = async (requiredFrom: string | null) => {
  const { data } = await api.put('/cost-allocation/config', { requiredFrom });
  return data;
};

export const createAllocCostCenter = async (payload: {
  code: string; name: string; typeCode: string; parentId?: number | null; externalRef?: string | null; notes?: string | null; sortOrder?: number; departmentId?: number | null;
}) => {
  const { data } = await api.post('/cost-allocation', payload);
  return data;
};

export const updateAllocCostCenter = async (id: number, payload: {
  code?: string; name?: string; externalRef?: string | null; notes?: string | null; sortOrder?: number; isActive?: boolean; departmentId?: number | null; cascade?: boolean;
  ifCodeExists?: 'rename_old' | 'take_over';
}) => {
  const { data } = await api.put(`/cost-allocation/${id}`, payload);
  return data;
};

export const upsertCostCenterType = async (payload: Partial<CostCenterType> & { code: string; label: string }) => {
  const { data } = await api.post('/cost-allocation/types', payload);
  return data;
};

export interface CostCenterImportResult {
  dryRun: boolean;
  source: 'id_register' | 'import';
  added: Array<{ code: string; label: string }>;
  updated: Array<{ code: string; label: string; changes: string[] }>;
  unchanged: number;
  errors: Array<{ row: string; message: string }>;
  logId?: number;
}

export const importCostCenters = async (kind: 'id-register' | 'template' | 'status', file: File, commit: boolean): Promise<CostCenterImportResult> => {
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await api.post(`/cost-allocation/import/${kind}`, fd, {
    params: commit ? { commit: 1 } : undefined,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const downloadCostCenterStatusSheet = async () => {
  const response = await api.get('/cost-allocation/status.xlsx', { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `cost-center-status-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const listCostCenterImports = async () => {
  const { data } = await api.get('/cost-allocation/imports');
  return data;
};

export const setLineCostCenter = async (mrLineId: number, costCenterId: number | null) => {
  const { data } = await api.patch(`/cost-allocation/lines/${mrLineId}`, { costCenterId });
  return data;
};

export const bulkSetLineCostCenter = async (mrLineIds: number[], costCenterId: number | null): Promise<Array<{ mrLineId: number; ok: boolean; error?: string }>> => {
  const { data } = await api.post('/cost-allocation/lines/bulk', { mrLineIds, costCenterId });
  return data;
};

export const listUnallocatedLines = async (params?: { departmentIds?: number[]; status?: string; search?: string; fields?: string[]; limit?: number; offset?: number }) => {
  const { data } = await api.get('/cost-allocation/lines/unallocated', {
    params: {
      ...params,
      departmentIds: params?.departmentIds?.length ? params.departmentIds.join(',') : undefined,
      fields: params?.fields?.length ? params.fields.join(',') : undefined,
    },
  });
  return data as { total: number; rows: any[] };
};

export interface GlobalSearchHit { type: string; id: number | string; title: string; subtitle?: string; url?: string; meta?: Record<string, any> }
export interface GlobalSearchGroup { type: string; label: string; items: GlobalSearchHit[] }
export const globalSearch = async (q: string, types?: string[]): Promise<{ groups: GlobalSearchGroup[]; tookMs: number }> => {
  const { data } = await api.get('/search', { params: { q, types: types?.length ? types.join(',') : undefined } });
  return data;
};

export const listSearchFields = async (): Promise<Array<{ key: string; label: string }>> => {
  const { data } = await api.get('/cost-allocation/lines/search-fields');
  return data;
};

export interface CostAllocationReportParams {
  from?: string; to?: string; costCenterId?: number; departmentId?: number; departmentIds?: number[]; unallocatedOnly?: boolean; includeUnordered?: boolean; stages?: string[];
}

const reportParams = (p?: CostAllocationReportParams) => ({
  from: p?.from || undefined,
  to: p?.to || undefined,
  costCenterId: p?.costCenterId || undefined,
  departmentId: p?.departmentId || undefined,
  departmentIds: p?.departmentIds?.length ? p.departmentIds.join(',') : undefined,
  unallocatedOnly: p?.unallocatedOnly ? 1 : undefined,
  includeUnordered: p?.includeUnordered ? 1 : undefined,
  stages: p?.stages?.length ? p.stages.join(',') : undefined,
});

export const getCostAllocationReport = async (params?: CostAllocationReportParams) => {
  const { data } = await api.get('/cost-allocation/report', { params: reportParams(params) });
  return data as { rows: any[]; summary: any[]; byCategory: any[]; totals: { lines: number; cost: number; unallocated: number } };
};

export const downloadCostAllocationReport = async (params?: CostAllocationReportParams) => {
  const response = await api.get('/cost-allocation/report.xlsx', { params: reportParams(params), responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `cost-allocation-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
